import { Translate } from '@chipinside/translate'

import { Permissions, Plan } from '#/@types/backend'

/**
 * `guest` cobre as telas que existem fora de qualquer tenant (logout, manutenção, 404).
 * Como `useAppContext()` só devolve `farm | company | system`, elas nunca entram no
 * `getAvailable({ context })` da busca avançada.
 */
export type PagesListContext = 'farm' | 'system' | 'company' | 'guest'

export type PagesListPermissions = {
  farm: Record<Permissions, boolean>
  company: Record<Permissions, boolean>
  system: Record<Permissions, boolean>
}

export type PageConditions = {
  permissions: PagesListPermissions
  farm_count: boolean
  has_company: boolean
  secondPregnancy: boolean
  thirdPregnancy: boolean
  is_female: boolean
  productionType: string
  point: string
  farm_plan: Plan
  context?: PagesListContext
}

type PagePermission = (params: PageConditions) => boolean

/**
 * Categoria da página na busca avançada: `analytics` (Cow Analytics), `lists` (Listas) ou
 * `resources` (demais páginas). Preenchida manualmente por entrada em `getMapPages()`.
 */
export type PageCategory = 'analytics' | 'lists' | 'resources'

export type PageByIdEntry = {
  title: string
  subtitle?: string
  description?: string
  url: string
  enabled: PagePermission
  context: PagesListContext
  category: PageCategory
  crumbs: string[]
  track: string
}

/**
 * Fonte de verdade das páginas, indexada por `id`. `satisfies` (em vez de anotação de retorno)
 * preserva as chaves literais do objeto, usadas por `PageId` para tipar ids de página com
 * autocomplete/checagem de typo.
 */

const getMapPages = () =>
  ({
    'farm-animal-batch-history': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'herd' }), 'farm-herd-animals', '{animal}'],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_animal,
      title: Translate({
        messageKey: 'item_history',
        params: { item: Translate({ lower: true, messageKey: 'batch_plural' }) },
      }),
      track: 'Animais / Animal / Histórico de lotes',
      url: '/farm/{farm}/modules/herd/animals/{animal}/batch-history',
    },
    'farm-animal-birth-index': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'herd' }), 'farm-herd-animals', '{animal}'],
      description: '',
      enabled: ({ is_female, permissions }) =>
        is_female !== false && permissions.farm.view_birth_index_graph,
      title: Translate({
        messageKey: 'item_index',
        params: { item: Translate({ lower: true, messageKey: 'calving' }) },
      }),
      track: 'Animais / Animal / Índice de parto',
      url: '/farm/{farm}/modules/herd/animals/{animal}/birth-index',
    },
    'farm-animal-body-assessment-history': {
      category: 'lists',
      context: 'farm',
      crumbs: ['{farm}', 'farm-lists', Translate({ messageKey: 'rearing_raising' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_animal_weight,
      title: Translate({
        messageKey: 'item_history',
        params: { item: Translate({ lower: true, messageKey: 'body_assessment' }) },
      }),
      track: 'Cria/Recria / Avaliação corporal / Histórico',
      url: '/farm/{farm}/lists/rearing/weights',
    },
    'farm-animal-breathing-index': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'herd' }), 'farm-herd-animals', '{animal}'],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.view_breathing_index_graph ||
        permissions.farm.view_reproductive_fitness_by_heat_stress_graph,
      title: Translate({
        messageKey: 'item_index',
        params: { item: Translate({ lower: true, messageKey: 'breathing' }) },
      }),
      track: 'Animais / Animal / Índice de respiração',
      url: '/farm/{farm}/modules/herd/animals/{animal}/breathing-index',
    },
    'farm-animal-collar-history': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'herd' }), 'farm-herd-animals', '{animal}'],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_collar,
      title: Translate({
        messageKey: 'item_history',
        params: { item: Translate({ lower: true, messageKey: 'collar_plural' }) },
      }),
      track: 'Animais / Animal / Histórico de coleiras',
      url: '/farm/{farm}/modules/herd/animals/{animal}/collar-history',
    },
    'farm-animal-daily-monitoring': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'herd' }), 'farm-herd-animals', '{animal}'],
      description: '',
      enabled: ({ farm_plan, permissions }) =>
        farm_plan !== 'cow-connect' &&
        (permissions.system.view_animal_accumulated_graph ||
          permissions.farm.view_animal_accumulated_graph),
      title: Translate({ messageKey: 'day_monitoring' }),
      track: 'Animais / Animal / Monitoramento dia',
      url: '/farm/{farm}/modules/herd/animals/{animal}',
    },
    'farm-animal-health': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'herd' }), 'farm-herd-animals', '{animal}'],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_health_event,
      title: Translate({ messageKey: 'health' }),
      track: 'Animais / Animal / Saúde',
      url: '/farm/{farm}/modules/herd/animals/{animal}/health',
    },
    'farm-animal-health-index': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'herd' }), 'farm-herd-animals', '{animal}'],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_health_index_graph,
      title: Translate({
        messageKey: 'item_index',
        params: { item: Translate({ lower: true, messageKey: 'health' }) },
      }),
      track: 'Animais / Animal / Índice de saúde',
      url: '/farm/{farm}/modules/herd/animals/{animal}/health-index',
    },
    'farm-animal-heat-index': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'herd' }), 'farm-herd-animals', '{animal}'],
      description: '',
      enabled: ({ is_female, permissions }) =>
        is_female !== false &&
        (permissions.farm.view_heat_index_graph || permissions.farm.view_heat_index_farm_graph),
      title: Translate({
        messageKey: 'item_index',
        params: { item: Translate({ lower: true, messageKey: 'heat' }) },
      }),
      track: 'Animais / Animal / Índice de cio',
      url: '/farm/{farm}/modules/herd/animals/{animal}/heat-index',
    },
    'farm-animal-hourly-monitoring': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'herd' }), 'farm-herd-animals', '{animal}'],
      description: '',
      enabled: ({ farm_plan, permissions }) =>
        farm_plan !== 'cow-connect' && permissions.farm.view_animal_hourly_graph,
      title: Translate({ messageKey: 'hourly_monitoring' }),
      track: 'Animais / Animal / Monitoramento hora',
      url: '/farm/{farm}/modules/herd/animals/{animal}/hourly',
    },
    'farm-animal-notes': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'herd' }), 'farm-herd-animals', '{animal}'],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_animal_note,
      title: Translate({ messageKey: 'note_plural' }),
      track: 'Animais / Animal / Notas',
      url: '/farm/{farm}/modules/herd/animals/{animal}/notes',
    },
    'farm-animal-production': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'herd' }), 'farm-herd-animals', '{animal}'],
      description: '',
      enabled: ({ is_female, permissions }) =>
        is_female !== false &&
        permissions.farm.list_milk_production &&
        permissions.farm.list_milk_quality,
      title: Translate({ messageKey: 'production' }),
      track: 'Animais / Animal / Produção',
      url: '/farm/{farm}/modules/herd/animals/{animal}/production',
    },
    'farm-animal-reproduction': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'herd' }), 'farm-herd-animals', '{animal}'],
      description: '',
      enabled: ({ is_female, permissions }) =>
        is_female !== false && permissions.farm.list_reproduction_event,
      title: Translate({ messageKey: 'reproduction' }),
      track: 'Animais / Animal / Reprodução',
      url: '/farm/{farm}/modules/herd/animals/{animal}/reproduction',
    },
    'farm-animal-weighing': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'herd' }), 'farm-herd-animals', '{animal}'],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_animal_weight,
      title: Translate({ messageKey: 'body_assessment' }),
      track: 'Animais / Animal / Avaliação corporal',
      url: '/farm/{farm}/modules/herd/animals/{animal}/body-assessment',
    },
    'farm-batch-accumulated': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'herd' }), 'farm-herd-batches', '{batch}'],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_average_batch_daily,
      title: Translate({ messageKey: 'accumulated' }),
      track: 'Rebanho / Lote / Acumulado',
      url: '/farm/{farm}/modules/herd/batch/{batch}/accumulated',
    },
    'farm-batch-diet': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'herd' }), 'farm-herd-batches', '{batch}'],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_diet,
      title: Translate({ messageKey: 'batch_diet' }),
      track: 'Rebanho / Lote / Dieta do lote',
      url: '/farm/{farm}/modules/herd/batch/{batch}/diet',
    },
    'farm-batch-monitoring': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'herd' }), 'farm-herd-batches', '{batch}'],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_batch || permissions.farm.list_batch,
      title: Translate({ messageKey: 'batch_monitoring' }),
      track: 'Rebanho / Lote / Monitoramento do lote',
      url: '/farm/{farm}/modules/herd/batch/{batch}/monitoring',
    },
    'farm-batch-profile-consumption': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'herd' }), 'farm-herd-batches', '{batch}'],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.view_batch_intake_graph ||
        permissions.farm.view_temperature_history ||
        permissions.farm.view_rumination_profile ||
        permissions.farm.view_panting_profile,
      title: Translate({ messageKey: 'intake_and_panting' }),
      track: 'Rebanho / Lote / Perfil e consumo',
      url: '/farm/{farm}/modules/herd/batch/{batch}/profile',
    },
    'farm-batch-review-animals': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'herd' }), 'farm-herd-batches', '{batch}'],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_animal_accumulated_graph,
      title: Translate({ messageKey: 'animals_for_review' }),
      track: 'Rebanho / Lote / Animais para revisão',
      url: '/farm/{farm}/modules/herd/batch/{batch}/review',
    },
    'farm-batch-variability': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'herd' }), 'farm-herd-batches', '{batch}'],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_batch_monitoring,
      title: Translate({ messageKey: 'variability' }),
      track: 'Rebanho / Lote / Variabilidade',
      url: '/farm/{farm}/modules/herd/batch/{batch}/variability',
    },
    'farm-cow-analytics': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}'],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.view_wellness_analytics ||
        permissions.farm.view_herd_analytics ||
        permissions.farm.view_conception_analytics ||
        (permissions.farm.view_calf_analytics && permissions.farm.list_animal_weight) ||
        permissions.farm.view_calf_analytics ||
        permissions.farm.view_heat_return_analytics ||
        (permissions.farm.list_animal && permissions.farm.list_reproduction_event) ||
        permissions.farm.view_health_analytics ||
        permissions.farm.list_reproduction_event ||
        permissions.farm.view_transition_analytics ||
        permissions.farm.view_discard_analytics ||
        permissions.farm.view_service_analytics,
      title: Translate({ messageKey: 'cow_analytics' }),
      track: 'Cow analytics',
      url: '/farm/{farm}/cow-analytics',
    },
    'farm-cow-analytics-conception-per-bull-semen': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'conception' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_conception_analytics,
      title: Translate({
        messageKey: 'per_item',
        params: { item: Translate({ lower: true, messageKey: 'bull_or_semen' }) },
      }),
      track: 'Cow analytics / Concepção / Por touro/sêmen',
      url: '/farm/{farm}/cow-analytics/conception/per-bull-semen',
    },
    'farm-cow-analytics-conception-per-days-in-lactation': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'conception' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_conception_analytics,
      title: Translate({
        messageKey: 'per_item',
        params: { item: Translate({ messageKey: 'DEL' }) },
      }),
      track: 'Cow analytics / Concepção / Por DEL',
      url: '/farm/{farm}/cow-analytics/conception/per-del',
    },
    'farm-cow-analytics-conception-per-days-open': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'conception' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_conception_analytics,
      title: Translate({ messageKey: 'days_open' }),
      track: 'Cow analytics / Concepção / Dias em aberto',
      url: '/farm/{farm}/cow-analytics/conception/per-dea',
    },
    'farm-cow-analytics-conception-per-inseminator': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'conception' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_conception_analytics,
      title: Translate({
        messageKey: 'per_item',
        params: { item: Translate({ lower: true, messageKey: 'inseminator' }) },
      }),
      track: 'Cow analytics / Concepção / Por inseminador',
      url: '/farm/{farm}/cow-analytics/conception/per-inseminator',
    },
    'farm-cow-analytics-conception-per-number-of-services': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'conception' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_conception_analytics,
      title: Translate({
        messageKey: 'per_item',
        params: { item: Translate({ lower: true, messageKey: 'number_of_services' }) },
      }),
      track: 'Cow analytics / Concepção / Por número de serviços',
      url: '/farm/{farm}/cow-analytics/conception/per-services',
    },
    'farm-cow-analytics-conception-per-service-type': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'conception' })],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.view_conception_analytics && permissions.farm.view_service_analytics,
      title: Translate({
        messageKey: 'per_item',
        params: { item: Translate({ lower: true, messageKey: 'service_type' }) },
      }),
      track: 'Cow analytics / Concepção / Por tipo de serviço',
      url: '/farm/{farm}/cow-analytics/conception/per-type',
    },
    'farm-cow-analytics-conception-rate': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'conception' })],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.view_conception_analytics && permissions.farm.view_service_analytics,
      title: Translate({ messageKey: 'conception_rate' }),
      track: 'Cow analytics / Concepção / Taxa de concepção',
      url: '/farm/{farm}/cow-analytics/conception/rate',
    },
    'farm-cow-analytics-discard-marked-for-discard': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'animal_discard' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_discard_analytics,
      title: Translate({ messageKey: 'marked_for_discard' }),
      track: 'Cow analytics / Descarte de animais / Marcada para descarte',
      url: '/farm/{farm}/cow-analytics/discard/marked',
    },
    'farm-cow-analytics-discard-moment': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'animal_discard' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_discard_analytics,
      title: Translate({ messageKey: 'discard_moment' }),
      track: 'Cow analytics / Descarte de animais / Momento do Descarte',
      url: '/farm/{farm}/cow-analytics/discard/moment',
    },
    'farm-cow-analytics-discard-rate': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'animal_discard' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_discard_analytics,
      title: Translate({ messageKey: 'discard_rate' }),
      track: 'Cow analytics / Descarte de animais / Taxa de descarte',
      url: '/farm/{farm}/cow-analytics/discard/rate',
    },
    'farm-cow-analytics-discard-reason': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'animal_discard' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_discard_analytics,
      title: Translate({ messageKey: 'discard_reason' }),
      track: 'Cow analytics / Descarte de animais / Motivo de descarte',
      url: '/farm/{farm}/cow-analytics/discard/reason',
    },
    'farm-cow-analytics-discard-total': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'animal_discard' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_discard_analytics,
      title: Translate({ messageKey: 'discard_total' }),
      track: 'Cow analytics / Descarte de animais / Total de descarte',
      url: '/farm/{farm}/cow-analytics/discard/total',
    },
    'farm-cow-analytics-gestational-loss-rate': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'gestational_loss' })],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.view_service_analytics && permissions.farm.view_conception_analytics,
      title: Translate({ messageKey: 'loss_rate' }),
      track: 'Cow analytics / Perdas gestacionais / Taxa de perdas',
      url: '/farm/{farm}/cow-analytics/gestational/rate',
    },
    'farm-cow-analytics-gestational-losses-by-dim': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'gestational_loss' })],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.view_service_analytics && permissions.farm.view_conception_analytics,
      title: Translate({ messageKey: 'dim_losses' }),
      track: 'Cow analytics / Perdas gestacionais / Perdas por del',
      url: '/farm/{farm}/cow-analytics/gestational/dim',
    },
    'farm-cow-analytics-gestational-losses-per-bull': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'gestational_loss' })],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.view_service_analytics && permissions.farm.view_conception_analytics,
      title: Translate({ messageKey: 'bull_losses' }),
      track: 'Cow analytics / Perdas gestacionais / Perdas por touro',
      url: '/farm/{farm}/cow-analytics/gestational/bull',
    },
    'farm-cow-analytics-gestational-monthly-losses': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'gestational_loss' })],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.view_service_analytics && permissions.farm.view_conception_analytics,
      title: Translate({ messageKey: 'monthly_losses' }),
      track: 'Cow analytics / Perdas gestacionais / Perdas mensais',
      url: '/farm/{farm}/cow-analytics/gestational/monthly',
    },
    'farm-cow-analytics-gestational-period-losses': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'gestational_loss' })],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.view_service_analytics && permissions.farm.view_conception_analytics,
      title: Translate({ messageKey: 'stage_losses' }),
      track: 'Cow analytics / Perdas gestacionais / Perdas por período',
      url: '/farm/{farm}/cow-analytics/gestational/stage',
    },
    'farm-cow-analytics-gestational-registered-losses': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'gestational_loss' })],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.view_service_analytics && permissions.farm.view_conception_analytics,
      title: Translate({ messageKey: 'registered_losses' }),
      track: 'Cow analytics / Perdas gestacionais / Perdas registradas',
      url: '/farm/{farm}/cow-analytics/gestational/list',
    },
    'farm-cow-analytics-health-alert-count': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'health' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_health_analytics,
      title: Translate({ messageKey: 'alert_count' }),
      track: 'Cow analytics / Saúde / Contagem de alertas',
      url: '/farm/{farm}/cow-analytics/health/alert-count',
    },
    'farm-cow-analytics-health-history': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'health' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_health_analytics,
      title: Translate({
        messageKey: 'item_history',
        params: { item: Translate({ lower: true, messageKey: 'case' }) },
      }),
      track: 'Cow analytics / Saúde / Histórico de casos',
      url: '/farm/{farm}/cow-analytics/health/history',
    },
    'farm-cow-analytics-health-hospital-time': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'health' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_health_analytics,
      title: Translate({ messageKey: 'hospital_time' }),
      track: 'Cow analytics / Saúde / Tempo de enfermaria',
      url: '/farm/{farm}/cow-analytics/health/hospital-time',
    },
    'farm-cow-analytics-health-mastitis': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'health' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_health_analytics,
      title: Translate({ messageKey: 'mastitis' }),
      track: 'Cow analytics / Saúde / Mastite',
      url: '/farm/{farm}/cow-analytics/health/mastitis',
    },
    'farm-cow-analytics-health-mastitis-dynamics': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'health' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_health_analytics,
      title: Translate({ messageKey: 'mastitis_dynamics' }),
      track: 'Cow analytics / Saúde / Dinâmica de mastite',
      url: '/farm/{farm}/cow-analytics/health/mastitis-dynamics',
    },
    'farm-cow-analytics-health-occurrences': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'health' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_health_analytics,
      title: Translate({ messageKey: 'total_occurrences' }),
      track: 'Cow analytics / Saúde / Total de ocorrências',
      url: '/farm/{farm}/cow-analytics/health/occurrences',
    },
    'farm-cow-analytics-heat-monitoring-alert-hourly': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'heat_monitoring' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_heat_monitoring_analytics,
      title: Translate({ messageKey: 'alert_hourly' }),
      track: 'Cow analytics / Monitoramento de cio / Alertas por horário',
      url: '/farm/{farm}/cow-analytics/heat-monitoring/alert-hourly',
    },
    'farm-cow-analytics-heat-monitoring-alert-utilization': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'heat_monitoring' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_heat_monitoring_analytics,
      title: Translate({ messageKey: 'alert_utilization' }),
      track: 'Cow analytics / Monitoramento de cio / Aproveitamento de alertas',
      url: '/farm/{farm}/cow-analytics/heat-monitoring/alert-utilization',
    },
    'farm-cow-analytics-heat-monitoring-cycling-rate': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'heat_monitoring' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_heat_monitoring_analytics,
      title: Translate({ messageKey: 'cycling_rate' }),
      track: 'Cow analytics / Monitoramento de cio / Cycling Rate',
      url: '/farm/{farm}/cow-analytics/heat-monitoring/cycling-rate',
    },
    'farm-cow-analytics-heat-monitoring-cycling-rate-accumulated': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'heat_monitoring' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_heat_monitoring_analytics,
      title: Translate({ messageKey: 'cycling_rate_accumulated' }),
      track: 'Cow analytics / Monitoramento de cio / Cycling Rate Acumulado',
      url: '/farm/{farm}/cow-analytics/heat-monitoring/cycling-rate-accumulated',
    },
    'farm-cow-analytics-heat-monitoring-heat-return': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'heat_monitoring' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_heat_monitoring_heat_return_analytics,
      title: Translate({ messageKey: 'return_heats' }),
      track: 'Cow analytics / Monitoramento de cio / Cios de retorno',
      url: '/farm/{farm}/cow-analytics/heat-monitoring/heat-return',
    },
    'farm-cow-analytics-heat-return': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics'],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_heat_return_analytics,
      title: Translate({ messageKey: 'heat_return' }),
      track: 'Cow analytics / Retorno de cio',
      url: '/farm/{farm}/cow-analytics/heat-return',
    },
    'farm-cow-analytics-herd-average-dim': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'herd' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_herd_analytics,
      title: Translate({ messageKey: 'average_DEL' }),
      track: 'Cow analytics / Rebanho / DEL médio',
      url: '/farm/{farm}/cow-analytics/herd/average-dim',
    },
    'farm-cow-analytics-herd-general': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'herd' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_herd_analytics,
      title: Translate({ messageKey: 'general_herd' }),
      track: 'Cow analytics / Rebanho / Rebanho geral',
      url: '/farm/{farm}/cow-analytics/herd/general',
    },
    'farm-cow-analytics-herd-lactating-animals': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'herd' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_herd_analytics,
      title: Translate({ messageKey: 'lactating_animals' }),
      track: 'Cow analytics / Rebanho / Animais em lactação',
      url: '/farm/{farm}/cow-analytics/herd/lactating-animals',
    },
    'farm-cow-analytics-herd-lactating-projection': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'herd' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_herd_analytics,
      title: Translate({ messageKey: 'projection_lactating_cows' }),
      track: 'Cow analytics / Rebanho / Projeção de vacas em lactação',
      url: '/farm/{farm}/cow-analytics/herd/lactating-projection',
    },
    'farm-cow-analytics-herd-replacement-rate': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'herd' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_herd_analytics,
      title: Translate({ messageKey: 'replacement_rate' }),
      track: 'Cow analytics / Rebanho / Taxa de reposição',
      url: '/farm/{farm}/cow-analytics/herd/replacement',
    },
    'farm-cow-analytics-herd-reproductive-distribution': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'herd' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_herd_analytics,
      title: Translate({ messageKey: 'reproductive_distribution' }),
      track: 'Cow analytics / Rebanho / Distribuição reprodutiva',
      url: '/farm/{farm}/cow-analytics/herd/reproductive-distribution',
    },
    'farm-cow-analytics-marked-all-markings': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'marked_as' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_service_analytics,
      title: Translate({ messageKey: 'all_markings' }),
      track: 'Cow analytics / Marcadas como / Todas marcações',
      url: '/farm/{farm}/cow-analytics/marked-as',
    },
    'farm-cow-analytics-marked-for-discard': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'marked_as' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_service_analytics,
      title: Translate({ messageKey: 'for_discard' }),
      track: 'Cow analytics / Marcadas como / Para descarte',
      url: '/farm/{farm}/cow-analytics/marked-as/discard',
    },
    'farm-cow-analytics-marked-for-lactation-induction': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'marked_as' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_service_analytics,
      title: Translate({ messageKey: 'for_induction_lactation' }),
      track: 'Cow analytics / Marcadas como / Para indução de lactação',
      url: '/farm/{farm}/cow-analytics/marked-as/induction',
    },
    'farm-cow-analytics-milk-control-general': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'milk_control' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_milk_production,
      title: Translate({ messageKey: 'general_production' }),
      track: 'Cow analytics / Controle leiteiro / Produção geral',
      url: '/farm/{farm}/cow-analytics/milk-control/general',
    },
    'farm-cow-analytics-milk-control-ranking': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'milk_control' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_milk_production,
      title: Translate({ messageKey: 'productive_ranking' }),
      track: 'Cow analytics / Controle leiteiro / Ranking produtivo',
      url: '/farm/{farm}/cow-analytics/milk-control/ranking',
    },
    'farm-cow-analytics-milk-control-tank-analysis': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'milk_control' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_milk_quality,
      title: Translate({ messageKey: 'tank_analysis' }),
      track: 'Cow analytics / Controle leiteiro / Análise de tanque',
      url: '/farm/{farm}/cow-analytics/milk-control/tank-analysis',
    },
    'farm-cow-analytics-rearing-average-birth-weight': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'rearing_raising' })],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.view_calf_analytics && permissions.farm.list_animal_weight,
      title: Translate({ messageKey: 'average_birth_weight' }),
      track: 'Cow analytics / Cria/Recria / Peso médio nascimento',
      url: '/farm/{farm}/cow-analytics/rearing/birth',
    },
    'farm-cow-analytics-rearing-average-daily-gain': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'rearing_raising' })],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.view_calf_analytics && permissions.farm.list_animal_weight,
      title: Translate({ messageKey: 'average_daily_gain' }),
      track: 'Cow analytics / Cria/Recria / Ganho médio diário',
      url: '/farm/{farm}/cow-analytics/rearing/gain',
    },
    'farm-cow-analytics-rearing-average-weaning-weight': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'rearing_raising' })],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.view_calf_analytics && permissions.farm.list_animal_weight,
      title: Translate({ messageKey: 'average_weaning_weight' }),
      track: 'Cow analytics / Cria/Recria / Peso médio ao desaleitamento',
      url: '/farm/{farm}/cow-analytics/rearing/weaning',
    },
    'farm-cow-analytics-rearing-growth-curve': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'rearing_raising' })],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.view_calf_analytics && permissions.farm.list_animal_weight,
      title: Translate({ messageKey: 'growth_curve' }),
      track: 'Cow analytics / Cria/Recria / Curva de crescimento',
      url: '/farm/{farm}/cow-analytics/rearing/growth-curve',
    },
    'farm-cow-analytics-rearing-health': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'rearing_raising' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_calf_analytics,
      title: Translate({ messageKey: 'health' }),
      track: 'Cow analytics / Cria/Recria / Saúde',
      url: '/farm/{farm}/cow-analytics/rearing/health',
    },
    'farm-cow-analytics-rearing-mortality': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'rearing_raising' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_calf_analytics,
      title: Translate({ messageKey: 'mortality' }),
      track: 'Cow analytics / Cria/Recria / Mortalidade',
      url: '/farm/{farm}/cow-analytics/rearing/mortality',
    },
    'farm-cow-analytics-services-cycles': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'service_plural' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_service_analytics,
      title: Translate({ messageKey: 'service_cycles' }),
      track: 'Cow analytics / Serviços / Ciclos de serviços',
      url: '/farm/{farm}/cow-analytics/services/cycles',
    },
    'farm-cow-analytics-services-dim-first-service': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'service_plural' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_service_analytics,
      title: Translate({ messageKey: 'DIM_on_first_service' }),
      track: 'Cow analytics / Serviços / DEL no 1º serviço',
      url: '/farm/{farm}/cow-analytics/services/dim-first-service',
    },
    'farm-cow-analytics-services-dim-first-service-dispersion': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'service_plural' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_service_analytics,
      title: Translate({ messageKey: 'dim_first_service_dispersion' }),
      track: 'Cow analytics / Serviços / Dispersão 1º serviço',
      url: '/farm/{farm}/cow-analytics/services/dim-first-service-dispersion',
    },
    'farm-cow-analytics-services-interval': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'service_plural' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_service_analytics,
      title: Translate({ messageKey: 'interval_between_services' }),
      track: 'Cow analytics / Serviços / Intervalo entre serviços',
      url: '/farm/{farm}/cow-analytics/services/interval',
    },
    'farm-cow-analytics-services-monthly': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'service_plural' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_service_analytics,
      title: Translate({ messageKey: 'services_monthly' }),
      track: 'Cow analytics / Serviços / Serviços mensais',
      url: '/farm/{farm}/cow-analytics/services/monthly',
    },
    'farm-cow-analytics-services-rate': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'service_plural' })],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.view_service_analytics && permissions.farm.view_conception_analytics,
      title: Translate({ messageKey: 'service_rate' }),
      track: 'Cow analytics / Serviços / Taxa de serviço',
      url: '/farm/{farm}/cow-analytics/services/rate',
    },
    'farm-cow-analytics-transition-ppi': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'transition' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_transition_analytics,
      title: Translate({ messageKey: 'ppi_herd' }),
      track: 'Cow analytics / Transição / PPI do rebanho',
      url: '/farm/{farm}/cow-analytics/transition',
    },
    'farm-cow-analytics-wellness-nutrition-and-intake': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'general_wellness' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_wellness_analytics,
      title: Translate({ messageKey: 'nutrition_and_intake' }),
      track: 'Cow analytics / Bem-estar geral / Nutrição e consumo',
      url: '/farm/{farm}/cow-analytics/wellness/nutrition',
    },
    'farm-cow-analytics-wellness-thermal-comfort-and-rest': {
      category: 'analytics',
      context: 'farm',
      crumbs: ['{farm}', 'farm-cow-analytics', Translate({ messageKey: 'general_wellness' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_wellness_analytics,
      title: Translate({ messageKey: 'thermal_comfort_and_rest' }),
      track: 'Cow analytics / Bem-estar geral / Conforto térmico e descanso',
      url: '/farm/{farm}/cow-analytics/wellness/comfort',
    },
    'farm-dashboard': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}'],
      description: '',
      enabled: () => true, // Revisado
      title: Translate({ messageKey: 'dashboard' }),
      track: 'Dashboard',
      url: '/farm/{farm}',
    },
    'farm-export': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}'],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_farm_job,
      title: Translate({ messageKey: 'export_data' }),
      track: 'Exportar dados',
      url: '/farm/{farm}/export',
    },
    'farm-handling-events-record': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'handling_events' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_handling_event,
      title: Translate({ messageKey: 'handling_register' }),
      track: 'Eventos de manejo / Registro de manejo',
      url: '/farm/{farm}/modules/cattle-handling',
    },
    'farm-health-applications-performed': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'health' }),
        Translate({ messageKey: 'applications' }),
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_sanitary_protocol,
      title: Translate({ messageKey: 'performeds_female' }),
      track: 'Saúde / Aplicações / Realizadas',
      url: '/farm/{farm}/modules/health/applications/performed',
    },
    'farm-health-applications-scheduled': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'health' }),
        Translate({ messageKey: 'applications' }),
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_sanitary_protocol,
      title: Translate({ messageKey: 'scheduleds_female' }),
      track: 'Saúde / Aplicações / Programadas',
      url: '/farm/{farm}/modules/health/applications',
    },
    'farm-health-challenge-animals': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'health' })],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.list_health_event && permissions.farm.list_health_alert,
      title: Translate({ messageKey: 'challenged_animals' }),
      track: 'Saúde / Animais em desafio',
      url: '/farm/{farm}/modules/health/challenge',
    },
    'farm-health-infirmary-baby-diagnosis': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'health' }),
        Translate({ messageKey: 'raising_infirmary' }),
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_heifer_health_event,
      title: Translate({ messageKey: 'with_diagnosis' }),
      track: 'Saúde / Enfermaria Baby / Com diagnóstico',
      url: '/farm/{farm}/modules/health/infirmary-baby/diagnosis',
    },
    'farm-health-infirmary-baby-no-diagnosis': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'health' }),
        Translate({ messageKey: 'raising_infirmary' }),
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_heifer_health_event,
      title: Translate({ messageKey: 'without_diagnosis' }),
      track: 'Saúde / Enfermaria Baby / Sem diagnóstico',
      url: '/farm/{farm}/modules/health/infirmary-baby',
    },
    'farm-health-infirmary-baby-recovered': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'health' }),
        Translate({ messageKey: 'raising_infirmary' }),
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_heifer_health_event,
      title: Translate({ messageKey: 'recovered' }),
      track: 'Saúde / Enfermaria Baby / Recuperados',
      url: '/farm/{farm}/modules/health/infirmary-baby/recovered',
    },
    'farm-health-protocol-detail-applications': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'health' }),
        'farm-health-protocols',
        '{health_protocol}',
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_sanitary_protocol,
      title: Translate({ messageKey: 'applications' }),
      track: 'Saúde / Protocolos sanitários / Protocolo / Aplicações',
      url: '/farm/{farm}/modules/health/protocols/my-protocols/{health_protocol}/applications',
    },
    'farm-health-protocol-detail-behavior': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'health' }),
        'farm-health-protocols',
        '{health_protocol}',
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_sanitary_protocol,
      title: Translate({ messageKey: 'animal_behavior' }),
      track: 'Saúde / Protocolos sanitários / Protocolo / Comportamento animal',
      url: '/farm/{farm}/modules/health/protocols/my-protocols/{health_protocol}/behavior',
    },
    'farm-health-protocol-models': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'health' }),
        Translate({ messageKey: 'health_protocols' }),
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_sanitary_protocol_model,
      title: Translate({ messageKey: 'model_protocol' }),
      track: 'Saúde / Protocolos sanitários / Modelos de protocolo',
      url: '/farm/{farm}/modules/health/protocols/models',
    },
    'farm-health-protocol-models-analysis': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'health' }),
        'farm-health-protocol-models',
        '{health_protocol_model}',
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_sanitary_protocol_model,
      title: Translate({ messageKey: 'analysis' }),
      track: 'Saúde / Protocolos sanitários / Modelos de protocolo / Análises',
      url: '/farm/{farm}/modules/health/protocols/models/{health_protocol_model}/analysis',
    },
    'farm-health-protocol-models-cases': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'health' }),
        'farm-health-protocol-models',
        '{health_protocol_model}',
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_sanitary_protocol_model,
      title: Translate({ messageKey: 'treated_cases' }),
      track: 'Saúde / Protocolos sanitários / Modelos de protocolo / Casos tratados',
      url: '/farm/{farm}/modules/health/protocols/models/{health_protocol_model}/cases',
    },
    'farm-health-protocol-models-details': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'health' }),
        'farm-health-protocol-models',
        '{health_protocol_model}',
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_sanitary_protocol_model,
      title: Translate({ messageKey: 'protocol_details' }),
      track: 'Saúde / Protocolos sanitários / Modelos de protocolo / Detalhes do protocolo',
      url: '/farm/{farm}/modules/health/protocols/models/{health_protocol_model}/details',
    },
    'farm-health-protocols': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'health' }),
        Translate({ messageKey: 'health_protocols' }),
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_sanitary_protocol,
      title: Translate({ messageKey: 'my_protocols' }),
      track: 'Saúde / Protocolos sanitários / Meus protocolos',
      url: '/farm/{farm}/modules/health/protocols/my-protocols',
    },
    'farm-health-withdrawal-meat': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'health' }),
        Translate({ messageKey: 'animals_in_withdrawal' }),
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_health_event,
      title: Translate({
        messageKey: 'item_withdrawal',
        params: { item: Translate({ messageKey: 'meat' }) },
      }),
      track: 'Saúde / Animais em carência / Carência de Carne',
      url: '/farm/{farm}/modules/health/withdrawal/meat',
    },
    'farm-health-withdrawal-milk': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'health' }),
        Translate({ messageKey: 'animals_in_withdrawal' }),
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_health_event,
      title: Translate({
        messageKey: 'item_withdrawal',
        params: { item: Translate({ messageKey: 'milk' }) },
      }),
      track: 'Saúde / Animais em carência / Carência de leite',
      url: '/farm/{farm}/modules/health/withdrawal',
    },
    'farm-herd-animals': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'herd' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_animal,
      title: Translate({ messageKey: 'animal_plural' }),
      track: 'Rebanho / Animais',
      url: '/farm/{farm}/modules/herd/animals',
    },
    'farm-herd-batches': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'herd' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_batch,
      title: Translate({ messageKey: 'batch_plural' }),
      track: 'Rebanho / Lotes',
      url: '/farm/{farm}/modules/herd/batch',
    },
    'farm-herd-divergence': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'herd' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_batch_ccom_divergence,
      title: Translate({ messageKey: 'batch_divergence' }),
      track: 'Rebanho / Divergência de lotes',
      url: '/farm/{farm}/modules/herd/divergence',
    },
    'farm-herd-manage-batches': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'herd' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.relate_animal_batch,
      title: Translate({ messageKey: 'change_group_plural' }),
      track: 'Rebanho / Troca de lotes',
      url: '/farm/{farm}/modules/herd/manage',
    },
    'farm-import-detail-results': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'import_data' }), 'farm-import-imports'],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_farm_job,
      title: Translate({ messageKey: 'results' }),
      track: 'Importar dados / Importações / Detalhes da importação / Resultados',
      url: '/farm/{farm}/import/{import}/detail',
    },
    'farm-import-detail-unrecognized-data': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'import_data' }), 'farm-import-imports'],
      description: '',
      enabled: ({ permissions }) =>
        permissions.system.list_farm_job || permissions.farm.list_farm_job,
      title: Translate({ messageKey: 'data_not_recognized' }),
      track: 'Importar dados / Importações / Detalhes da importação / Dados não reconhecidos',
      url: '/farm/{farm}/import/{import}/detail/unrecognized-data',
    },
    'farm-import-files': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'import_data' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.manage_cowsync_uploaded_files,
      title: Translate({ messageKey: 'files' }),
      track: 'Importar dados / Arquivos',
      url: '/farm/{farm}/import/files',
    },
    'farm-import-imports': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'import_data' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_farm_job,
      title: Translate({ messageKey: 'imports' }),
      track: 'Importar dados / Importações',
      url: '/farm/{farm}/import',
    },
    'farm-infirmary-no-diagnosis': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'infirmary' })],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.list_health_event && permissions.farm.list_health_alert,
      title: Translate({ messageKey: 'without_diagnosis' }),
      track: 'Enfermaria / Sem diagnóstico',
      url: '/farm/{farm}/modules/health/infirmary',
    },
    'farm-infirmary-recovered': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'infirmary' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_health_event,
      title: Translate({ messageKey: 'recovered' }),
      track: 'Enfermaria / Recuperados',
      url: '/farm/{farm}/modules/health/infirmary/recovered',
    },
    'farm-infirmary-with-diagnosis': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'infirmary' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_health_event,
      title: Translate({ messageKey: 'with_diagnosis' }),
      track: 'Enfermaria / Com diagnóstico',
      url: '/farm/{farm}/modules/health/infirmary/diagnosis',
    },
    'farm-integration-aimer-overview': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', 'farm-integrations-available', Translate({ messageKey: 'aimer_cameras' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_camera_data,
      title: Translate({ messageKey: 'overview' }),
      track: 'Integrações / Câmeras Aimer / Visão geral',
      url: '/farm/{farm}/integrations/aimer',
    },
    'farm-integration-apcbrh_webleite-communications': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', 'farm-integrations-available', Translate({ messageKey: 'webleite' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_config_farm_integrations,
      title: Translate({ messageKey: 'communications' }),
      track: 'Integrações / Web + Leite / Comunicações',
      url: '/farm/{farm}/integrations/webleite/communications',
    },
    'farm-integration-apcbrh_webleite-overview': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', 'farm-integrations-available', Translate({ messageKey: 'webleite' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_config_farm_integrations,
      title: Translate({ messageKey: 'overview' }),
      track: 'Integrações / Web + Leite / Visão geral',
      url: '/farm/{farm}/integrations/webleite',
    },
    'farm-integration-apcbrh_webleite-settings': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', 'farm-integrations-available', Translate({ messageKey: 'webleite' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_config_farm_integrations,
      title: Translate({ messageKey: 'settings' }),
      track: 'Integrações / Web + Leite / Configurações',
      url: '/farm/{farm}/integrations/webleite/settings',
    },
    'farm-integrations-available': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'integrations' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_config_farm_integrations,
      title: Translate({ messageKey: 'available_integrations' }),
      track: 'Integrações / Integrações disponíveis',
      url: '/farm/{farm}/integrations',
    },
    'farm-integrations-services': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'integrations' })],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.list_config_farm_integrations || permissions.system.manage_farm_token,
      title: Translate({ messageKey: 'connected_services' }),
      track: 'Integrações / Serviços conectados',
      url: '/farm/{farm}/integrations/services',
    },
    'farm-lists': {
      category: 'lists',
      context: 'farm',
      crumbs: ['{farm}'],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_farm_lists,
      title: Translate({ messageKey: 'lists' }),
      track: 'Listas',
      url: '/farm/{farm}/lists',
    },
    'farm-lists-custom-list': {
      category: 'lists',
      context: 'farm',
      crumbs: ['{farm}', 'farm-lists'],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_farm_lists,
      title: Translate({ messageKey: 'custom_list' }),
      track: '',
      url: '/farm/{farm}/lists/custom/{custom_list}',
    },
    'farm-lists-discard-departure': {
      category: 'lists',
      context: 'farm',
      crumbs: ['{farm}', 'farm-lists', Translate({ messageKey: 'discard_plural' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_discard_analytics,
      title: Translate({ messageKey: 'animal_departure' }),
      track: 'Listas / Descarte / Saída de animais',
      url: '/farm/{farm}/lists/discard/departure',
    },
    'farm-lists-discard-marked': {
      category: 'lists',
      context: 'farm',
      crumbs: ['{farm}', 'farm-lists', Translate({ messageKey: 'discard_plural' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_service_analytics,
      title: Translate({ messageKey: 'for_discard' }),
      track: 'Listas / Descarte / Para descarte',
      url: '/farm/{farm}/lists/discard/marked',
    },
    'farm-lists-discard-report': {
      category: 'lists',
      context: 'farm',
      crumbs: ['{farm}', 'farm-lists', Translate({ messageKey: 'discard_plural' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_discard_analytics,
      title: Translate({ messageKey: 'discarded_animals' }),
      track: 'Listas / Descarte / Animais descartados',
      url: '/farm/{farm}/lists/discard/report',
    },
    'farm-lists-health-alerts': {
      category: 'lists',
      context: 'farm',
      crumbs: ['{farm}', 'farm-lists', Translate({ messageKey: 'health' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_health_analytics,
      title: Translate({ messageKey: 'health_alert_plural' }),
      track: 'Listas / Saúde / Alertas de saúde',
      url: '/farm/{farm}/lists/health/alerts',
    },
    'farm-lists-health-diagnosed': {
      category: 'lists',
      context: 'farm',
      crumbs: ['{farm}', 'farm-lists', Translate({ messageKey: 'health' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_health_analytics,
      title: Translate({ messageKey: 'diagnosed_animals' }),
      track: 'Listas / Saúde / Animais diagnosticados',
      url: '/farm/{farm}/lists/health/diagnosed',
    },
    'farm-lists-health-mastitis-history': {
      category: 'lists',
      context: 'farm',
      crumbs: ['{farm}', 'farm-lists', Translate({ messageKey: 'health' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_health_analytics,
      title: Translate({ messageKey: 'mastitis_history' }),
      track: 'Listas / Saúde / Histórico de mastite',
      url: '/farm/{farm}/lists/health/mastitis-history',
    },
    'farm-lists-milk-control-marked-as-induction': {
      category: 'lists',
      context: 'farm',
      crumbs: ['{farm}', 'farm-lists', Translate({ messageKey: 'milk_control' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_service_analytics,
      title: Translate({ messageKey: 'marked_for_induction_plural' }),
      track: 'Listas / Controle leiteiro / Marcados para indução de lactação',
      url: '/farm/{farm}/lists/milk-control/marked-as-induction',
    },
    'farm-lists-ppi-animal': {
      category: 'lists',
      context: 'farm',
      crumbs: ['{farm}', 'farm-lists'],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.list_animal && permissions.farm.list_reproduction_event,
      title: Translate({ messageKey: 'ppi_animal' }),
      track: 'Listas / Transição / Avaliação de PPI',
      url: '/farm/{farm}/lists/transition/ppi-animal',
    },
    'farm-lists-reproduction-critical-dim': {
      category: 'lists',
      context: 'farm',
      crumbs: ['{farm}', 'farm-lists', Translate({ messageKey: 'reproduction' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_service_analytics,
      title: Translate({ messageKey: 'critical_dim_without_pregnancy' }),
      track: 'Listas / Reprodução / Del alto sem prenhez',
      url: '/farm/{farm}/lists/reproduction/critical-dim',
    },
    'farm-lists-reproduction-eligible': {
      category: 'lists',
      context: 'farm',
      crumbs: ['{farm}', 'farm-lists', Translate({ messageKey: 'reproduction' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_service_analytics,
      title: Translate({ messageKey: 'eligible_cow_plural' }),
      track: 'Listas / Reprodução / Vacas aptas à reprodução',
      url: '/farm/{farm}/lists/reproduction/eligible',
    },
    'farm-lists-reproduction-heat-detected': {
      category: 'lists',
      context: 'farm',
      crumbs: ['{farm}', 'farm-lists', Translate({ messageKey: 'reproduction' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_service_analytics,
      title: Translate({ messageKey: 'heat_detected' }),
      track: 'Listas / Reprodução / Cios detectados',
      url: '/farm/{farm}/lists/reproduction/heat-detected',
    },
    'farm-lists-reproduction-losses': {
      category: 'lists',
      context: 'farm',
      crumbs: ['{farm}', 'farm-lists', Translate({ messageKey: 'reproduction' })],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.view_service_analytics && permissions.farm.view_conception_analytics,
      title: Translate({ messageKey: 'registered_losses' }),
      track: 'Listas / Reprodução / Perdas de prenhez registradas',
      url: '/farm/{farm}/lists/reproduction/losses',
    },
    'farm-lists-reproduction-marked-as-protocol': {
      category: 'lists',
      context: 'farm',
      crumbs: ['{farm}', 'farm-lists', Translate({ messageKey: 'reproduction' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_service_analytics,
      title: Translate({ messageKey: 'for_protocol' }),
      track: 'Listas / Reprodução / Marcadas para protocolo',
      url: '/farm/{farm}/lists/reproduction/marked-as-protocol',
    },
    'farm-lists-reproduction-marked-as-unfit': {
      category: 'lists',
      context: 'farm',
      crumbs: ['{farm}', 'farm-lists', Translate({ messageKey: 'reproduction' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_service_analytics,
      title: Translate({ messageKey: 'inapt_for_reproduction' }),
      track: 'Listas / Reprodução / Inaptas à reprodução',
      url: '/farm/{farm}/lists/reproduction/marked-as-unfit',
    },
    'farm-lists-reproduction-no-service': {
      category: 'lists',
      context: 'farm',
      crumbs: ['{farm}', 'farm-lists', Translate({ messageKey: 'reproduction' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_service_analytics,
      title: Translate({ messageKey: 'no_service' }),
      track: 'Listas / Reprodução / Sem serviço',
      url: '/farm/{farm}/lists/reproduction/no-service',
    },
    'farm-lists-reproduction-open': {
      category: 'lists',
      context: 'farm',
      crumbs: ['{farm}', 'farm-lists', Translate({ messageKey: 'reproduction' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_calf_analytics,
      title: Translate({ messageKey: 'open_apt_heifers' }),
      track: 'Listas / Reprodução / Novilhas vazias aos 15 meses',
      url: '/farm/{farm}/lists/reproduction/open',
    },
    'farm-lists-reproduction-services-performed': {
      category: 'lists',
      context: 'farm',
      crumbs: ['{farm}', 'farm-lists', Translate({ messageKey: 'reproduction' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_service_analytics,
      title: Translate({ messageKey: 'performed' }),
      track: 'Listas / Reprodução / Serviços realizados',
      url: '/farm/{farm}/lists/reproduction/services-performed',
    },
    'farm-milk-control-collected-milk': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'milk_control' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_milk_quality,
      title: Translate({ messageKey: 'collected_milk' }),
      track: 'Controle leiteiro / Leite coletado',
      url: '/farm/{farm}/modules/milk-control/collected',
    },
    'farm-milk-control-induced-marked': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'milk_control' }),
        Translate({ messageKey: 'lactation_induction' }),
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_service_analytics,
      title: Translate({ messageKey: 'marked_for_induction_plural' }),
      track: 'Controle leiteiro / Indução de lactação / Marcados para indução',
      url: '/farm/{farm}/modules/milk-control/induced/marked',
    },
    'farm-milk-control-induced-realized': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'milk_control' }),
        Translate({ messageKey: 'lactation_induction' }),
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_reproduction_event,
      title: Translate({ messageKey: 'inductions_realized' }),
      track: 'Controle leiteiro / Indução de lactação / Induções realizadas',
      url: '/farm/{farm}/modules/milk-control/induced/realized',
    },
    'farm-milk-control-production': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'milk_control' })],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.list_animal && permissions.farm.list_milk_production,
      title: Translate({ messageKey: 'production_register' }),
      track: 'Controle leiteiro / Registro da produção',
      url: '/farm/{farm}/modules/milk-control/production',
    },
    'farm-milk-control-production-per-animal': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'milk_control' }), 'farm-milk-control-production'],
      description: '',
      enabled: ({ permissions, productionType }) =>
        permissions.farm.list_milk_production && productionType === 'per_animal', // TODO
      title: Translate({ messageKey: 'individual_production' }),
      track: 'Controle leiteiro / Registro da produção / Produção individual',
      url: '/farm/{farm}/modules/milk-control/production/{production_per_animal}/per_animal',
    },
    'farm-milk-control-production-per-batch': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'milk_control' }), 'farm-milk-control-production'],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_milk_production,
      title: Translate({ messageKey: 'batch_average_plural' }),
      track: 'Controle leiteiro / Registro da produção / Media de lotes',
      url: '/farm/{farm}/modules/milk-control/production/{production_per_batch}/per_batch',
    },
    'farm-milk-control-quality': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'milk_control' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_milk_quality,
      title: Translate({ messageKey: 'tank_analysis' }),
      track: 'Controle leiteiro / Análise de tanque',
      url: '/farm/{farm}/modules/milk-control/quality',
    },
    'farm-monitoring-animals': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'monitoring' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_animal,
      title: Translate({
        messageKey: 'item_monitored_plural',
        params: { item: Translate({ lower: true, messageKey: 'animal_plural' }) },
      }),
      track: 'Monitoramento / Animais monitorados',
      url: '/farm/{farm}/modules/monitoring/animals',
    },
    'farm-monitoring-c-com': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'monitoring' })],
      description: '',
      enabled: ({ permissions }) =>
        permissions.system.list_ccom_at_farm ||
        permissions.system.list_ccom ||
        permissions.farm.list_ccom_at_farm ||
        permissions.farm.list_ccom,
      title: Translate({ messageKey: 'c_com_managment' }),
      track: 'Monitoramento / Gestão de antenas',
      url: '/farm/{farm}/modules/monitoring/c-com',
    },
    'farm-monitoring-c-tech-all': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'monitoring' }),
        Translate({ messageKey: 'collar_managment' }),
      ],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.list_collar ||
        permissions.farm.view_expired_cow_baby ||
        permissions.farm.list_collar_review_on_farm,
      title: Translate({ messageKey: 'all_female' }),
      track: 'Monitoramento / Gestão de coleira / Todas',
      url: '/farm/{farm}/modules/monitoring/c-tech',
    },
    'farm-monitoring-c-tech-animal-history': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'monitoring' }),
        Translate({ messageKey: 'collar_managment' }),
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_animal_collar,
      title: Translate({
        messageKey: 'item_history',
        params: { item: Translate({ lower: true, messageKey: 'animal_plural' }) },
      }),
      track: 'Monitoramento / Gestão de coleira / Histórico de animais',
      url: '/farm/{farm}/modules/monitoring/c-tech/details/{ctech}/history',
    },
    'farm-monitoring-c-tech-baby-expired': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'monitoring' }),
        Translate({ messageKey: 'collar_managment' }),
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_expired_cow_baby,
      title: Translate({ messageKey: 'cowbaby_expired' }),
      track: 'Monitoramento / Gestão de coleira / Baby expirada',
      url: '/farm/{farm}/modules/monitoring/c-tech/expired',
    },
    'farm-monitoring-c-tech-collar-report-map': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'monitoring' }),
        Translate({ messageKey: 'collar_managment' }),
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_monitoring,
      title: Translate({ messageKey: 'collar_map' }),
      track: 'Monitoramento / Gestão de coleira / Mapa de report de coleira',
      url: '/farm/{farm}/modules/monitoring/c-tech/details/{ctech}/map',
    },
    'farm-monitoring-c-tech-on-farm': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'monitoring' }),
        Translate({ messageKey: 'collar_managment' }),
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_collar_review_on_farm,
      title: Translate({ messageKey: 'farm_revision' }),
      track: 'Monitoramento / Gestão de coleira / Revisão na fazenda',
      url: '/farm/{farm}/modules/monitoring/c-tech/on-farm',
    },
    'farm-monitoring-c-tech-possible-removal': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'monitoring' }),
        Translate({ messageKey: 'collar_managment' }),
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_collar_review_on_farm,
      title: Translate({ messageKey: 'possible_removal' }),
      track: 'Monitoramento / Gestão de coleira / Possible_removal',
      url: '/farm/{farm}/modules/monitoring/c-tech/possible-removal',
    },
    'farm-monitoring-c-tech-replacements': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'monitoring' }),
        Translate({ messageKey: 'collar_managment' }),
      ],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.list_collar &&
        (permissions.farm.view_collar_status_report ||
          permissions.farm.list_collar_alert ||
          permissions.farm.list_farm_collar_alert),
      title: Translate({ messageKey: 'replace' }),
      track: 'Monitoramento / Gestão de coleira / Trocas',
      url: '/farm/{farm}/modules/monitoring/c-tech/replace',
    },
    'farm-monitoring-c-tech-technical-review': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'monitoring' }),
        Translate({ messageKey: 'collar_managment' }),
      ],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.list_collar &&
        (permissions.farm.view_collar_status_report || permissions.farm.list_collar_alert),
      title: Translate({ messageKey: 'tecnical_review' }),
      track: 'Monitoramento / Gestão de coleira / Revisão técnica',
      url: '/farm/{farm}/modules/monitoring/c-tech/tecnical',
    },
    'farm-monitoring-c-tech-waiting-bind': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'monitoring' }),
        Translate({ messageKey: 'collar_managment' }),
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_collar_review_on_farm,
      title: Translate({
        messageKey: 'waiting',
        params: { item: Translate({ lower: true, messageKey: 'attachment' }) },
      }),
      track: 'Monitoramento / Gestão de coleira / Aguardando vínculo',
      url: '/farm/{farm}/modules/monitoring/c-tech/waiting',
    },
    'farm-monitoring-cowcomfort': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'monitoring' }),
        Translate({ messageKey: ['cow_comfort_managment'] }),
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_cenv,
      title: Translate({ messageKey: 'cow_comfort' }),
      track: 'Monitoramento / Gestão de CowComfort / CowComfort',
      url: '/farm/{farm}/modules/monitoring/c-env',
    },
    'farm-monitoring-cowcomfort-points': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'monitoring' }),
        Translate({ messageKey: ['cow_comfort_managment'] }),
      ],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.list_cenv && permissions.farm.list_device_point,
      title: Translate({ messageKey: 'monitoring_point' }),
      track: 'Monitoramento / Gestão de CowComfort / Ponto de monitoramento',
      url: '/farm/{farm}/modules/monitoring/c-env/points',
    },
    'farm-monitoring-overview': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'monitoring' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_device_point,
      title: Translate({ messageKey: 'overview' }),
      track: 'Monitoramento / Visão geral',
      url: '/farm/{farm}/modules/monitoring/overview',
    },
    'farm-monitoring-tokenized-animals': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'monitoring' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_tokenizable_animals,
      title: Translate({ messageKey: 'tokenized_animals' }),
      track: ' Monitoramento / Animais tokenizados',
      url: '/farm/{farm}/modules/monitoring/tokenized',
    },
    'farm-notifications': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}'],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_alert,
      title: Translate({ messageKey: 'notification_plural' }),
      track: 'Notificações',
      url: '/farm/{farm}/modules/notifications',
    },
    'farm-nutrition-diet-catalog': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'nutrition' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_diet,
      title: Translate({
        messageKey: 'registration_of',
        params: { item: Translate({ lower: true, messageKey: 'diet_label' }) },
      }),
      track: 'Nutrição / Cadastro de dieta',
      url: '/farm/{farm}/modules/precision-nutrition/diet',
    },
    'farm-nutrition-ingredient-management': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'nutrition' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_ingredient,
      title: Translate({ messageKey: 'ingredient_management' }),
      track: 'Nutrição / Gestão de insumos',
      url: '/farm/{farm}/modules/precision-nutrition/ingredient',
    },
    'farm-nutrition-monitored-batches': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'nutrition' })],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.list_batch && permissions.farm.list_milk_production,
      title: Translate({
        messageKey: 'item_monitored_plural',
        params: { item: Translate({ lower: true, messageKey: 'batch_plural' }) },
      }),
      track: 'Nutrição / Lotes monitorados',
      url: '/farm/{farm}/modules/precision-nutrition/monitored',
    },
    'farm-precision-nutrition-monitored-camera': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'nutrition' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_camera_data,
      title: Translate({ messageKey: 'camera_batches' }),
      track: 'Nutrição / Lotes monitorados / Câmeras',
      url: '/farm/{farm}/modules/precision-nutrition/monitored/camera',
    },
    'farm-precision-nutrition-monitored-camera-detail': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'nutrition' }),
        'farm-precision-nutrition-monitored-camera',
        '{batch}',
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_camera_data,
      title: Translate({ messageKey: 'bunk_score' }),
      track: 'Nutrição / Lotes monitorados / Câmeras / Lote',
      url: '/farm/{farm}/modules/precision-nutrition/monitored/camera/{batch}',
    },
    'farm-rearing-weaning-executed': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'rearing_raising' }),
        Translate({ messageKey: 'weaning' }),
      ],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.list_animal && permissions.farm.list_reproduction_event,
      title: Translate({ messageKey: 'performed' }),
      track: 'Cria/Recria / Desaleitamento / Realizados',
      url: '/farm/{farm}/modules/rearing/weaning/executed',
    },
    'farm-rearing-weaning-scheduled': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'rearing_raising' }),
        Translate({ messageKey: 'weaning' }),
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_animal,
      title: Translate({ messageKey: 'programmed' }),
      track: 'Cria/Recria / Desaleitamento / Programados',
      url: '/farm/{farm}/modules/rearing/weaning',
    },
    'farm-reproduction-bull-semen': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'reproduction' })],
      description: '',
      enabled: ({ permissions }) =>
        permissions.system.list_farm_semen || permissions.farm.list_farm_semen,
      title: Translate({ messageKey: 'bull_or_semen' }),
      track: 'Reprodução / Touro/Sêmen',
      url: '/farm/{farm}/modules/reproduction/bull-semen',
    },
    'farm-reproduction-bull-semen-history': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'reproduction' }), 'farm-reproduction-bull-semen'],
      description: '',
      enabled: ({ permissions }) => permissions.farm.update_farm_semen,
      title: Translate({ messageKey: 'use_history' }),
      track: 'Reprodução / Touro/Sêmen / Histórico de uso',
      url: '/farm/{farm}/modules/reproduction/bull-semen/{semen}/history',
    },
    'farm-reproduction-bull-semen-new': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'reproduction' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_farm_new_semen,
      title: Translate({ messageKey: 'bull_or_semen' }) + ' (Nova)',
      track: 'Reprodução / Touro/Sêmen (Nova)',
      url: '/farm/{farm}/modules/reproduction/bull-semen-new',
    },
    'farm-reproduction-calving-iep': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'reproduction' }),
        Translate({ messageKey: 'calving_plural' }),
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_conception_analytics,
      title: Translate({ messageKey: 'calving_interval' }),
      track: 'Reprodução / Parto / IEP',
      url: '/farm/{farm}/modules/reproduction/calving/intervals',
    },
    'farm-reproduction-calving-performed': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'reproduction' }),
        Translate({ messageKey: 'calving_plural' }),
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_conception_analytics,
      title: Translate({ messageKey: 'performed' }),
      track: 'Reprodução / Parto / Realizados',
      url: '/farm/{farm}/modules/reproduction/calving/performed',
    },
    'farm-reproduction-calving-projection': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'reproduction' }),
        Translate({ messageKey: 'calving_plural' }),
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_conception_analytics,
      title: Translate({ messageKey: 'calving_projection' }),
      track: 'Reprodução / Parto / Projeção de partos',
      url: '/farm/{farm}/modules/reproduction/calving/projection',
    },
    'farm-reproduction-calving-scheduled': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'reproduction' }),
        Translate({ messageKey: 'calving_plural' }),
      ],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.list_animal && permissions.farm.list_reproduction_event,
      title: Translate({ messageKey: 'programmed' }),
      track: 'Reprodução / Parto / Programados',
      url: '/farm/{farm}/modules/reproduction/calving',
    },
    'farm-reproduction-drying-performed': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'reproduction' }),
        Translate({ messageKey: 'drying' }),
      ],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.list_animal && permissions.farm.list_reproduction_event,
      title: Translate({ messageKey: 'performed' }),
      track: 'Reprodução / Secagem / Realizados',
      url: '/farm/{farm}/modules/reproduction/drying/performed',
    },
    'farm-reproduction-drying-projection': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'reproduction' }),
        Translate({ messageKey: 'drying' }),
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_reproduction_event,
      title: Translate({ messageKey: 'drying_projection' }),
      track: 'Reprodução / Secagem / Projeção de secagens',
      url: '/farm/{farm}/modules/reproduction/drying/projection',
    },
    'farm-reproduction-drying-scheduled': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'reproduction' }),
        Translate({ messageKey: 'drying' }),
      ],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.list_animal && permissions.farm.list_reproduction_event,
      title: Translate({ messageKey: 'programmed' }),
      track: 'Reprodução / Secagem / Programados',
      url: '/farm/{farm}/modules/reproduction/drying',
    },
    'farm-reproduction-embryo-donor': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'reproduction' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_embryo_donor,
      title: Translate({ messageKey: 'donor_plural' }),
      track: 'Reprodução / Doadoras',
      url: '/farm/{farm}/modules/reproduction/embryo-donor',
    },
    'farm-reproduction-first-calving': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'reproduction' }),
        Translate({ messageKey: 'calving_plural' }),
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_conception_analytics,
      title: Translate({ messageKey: 'age_at_first_calving' }),
      track: 'Reprodução / Parto / Idade ao primeiro parto',
      url: '/farm/{farm}/modules/reproduction/calving/first-calving',
    },
    'farm-reproduction-gynecologic-exam': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'reproduction' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_animal,
      title: Translate({ messageKey: 'gynecologic_examination' }),
      track: 'Reprodução / Exame ginecológico',
      url: '/farm/{farm}/modules/reproduction/gynecologic',
    },
    'farm-reproduction-heat-alerts': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'reproduction' }),
        Translate({ messageKey: 'heat_alert_plural' }),
      ],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.list_animal &&
        permissions.farm.list_reproduction_alert &&
        permissions.farm.list_reproduction_event,
      title: Translate({ messageKey: 'alerted_plural' }),
      track: 'Reprodução / Alertas de cio / Alertados',
      url: '/farm/{farm}/modules/reproduction/heat',
    },
    'farm-reproduction-heat-confirmed': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'reproduction' }),
        Translate({ messageKey: 'heat_alert_plural' }),
      ],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.list_animal &&
        permissions.farm.list_reproduction_alert &&
        permissions.farm.list_reproduction_event,
      title: Translate({ messageKey: 'confirmed_plural' }),
      track: 'Reprodução / Alertas de cio / Confirmados',
      url: '/farm/{farm}/modules/reproduction/heat/confirmed',
    },
    'farm-reproduction-inseminator': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'reproduction' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_farm_inseminator,
      title: Translate({ messageKey: 'inseminator' }),
      track: 'Reprodução / Inseminador',
      url: '/farm/{farm}/modules/reproduction/inseminator',
    },
    'farm-reproduction-mating': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'reproduction' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_mating,
      title: Translate({ messageKey: 'mating' }),
      track: 'Reprodução / Acasalamento',
      url: '/farm/{farm}/modules/reproduction/mating',
    },
    'farm-reproduction-matrix': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'reproduction' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_matrix,
      title: Translate({ messageKey: 'matrix_plural' }),
      track: 'Reprodução / Matrizes',
      url: '/farm/{farm}/modules/reproduction/matrix',
    },
    'farm-reproduction-matrix-children': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'reproduction' }), 'farm-reproduction-matrix'],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_matrix && permissions.farm.list_animal,
      title: Translate({ messageKey: 'son_plural' }),
      track: 'Reprodução / Matrizes / Filhos',
      url: '/farm/{farm}/modules/reproduction/matrix/{matrix}/children',
    },
    'farm-reproduction-pre-calving': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'reproduction' })],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.list_animal && permissions.farm.list_reproduction_event,
      title: Translate({ messageKey: 'pre_calving' }),
      track: 'Reprodução / Pré-parto',
      url: '/farm/{farm}/modules/reproduction/pre-calving-prediction',
    },
    'farm-reproduction-pregnancy-check2': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'reproduction' }),
        Translate({ messageKey: 'pregnancy_diagnosis' }),
      ],
      description: '',
      enabled: ({ permissions, secondPregnancy }) =>
        permissions.farm.list_animal && permissions.farm.list_reproduction_event && secondPregnancy,
      title: Translate({
        messageKey: 'confirmation',
        params: { item: Translate({ messageKey: '2nd' }) },
      }),
      track: 'Reprodução / Diagnóstico de prenhez / 2ª confirmação',
      url: '/farm/{farm}/modules/reproduction/pregnancy/check2',
    },
    'farm-reproduction-pregnancy-check3': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'reproduction' }),
        Translate({ messageKey: 'pregnancy_diagnosis' }),
      ],
      description: '',
      enabled: ({ permissions, secondPregnancy, thirdPregnancy }) =>
        permissions.farm.list_animal &&
        permissions.farm.list_reproduction_event &&
        secondPregnancy &&
        thirdPregnancy,
      title: Translate({
        messageKey: 'confirmation',
        params: { item: Translate({ messageKey: '3rd' }) },
      }),
      track: 'Reprodução / Diagnóstico de prenhez / 3ª confirmação',
      url: '/farm/{farm}/modules/reproduction/pregnancy/check3',
    },
    'farm-reproduction-pregnancy-confirmation': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'reproduction' }),
        Translate({ messageKey: 'pregnancy_diagnosis' }),
      ],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.list_animal && permissions.farm.list_reproduction_event,
      title: Translate({ messageKey: 'confirmation' }),
      track: 'Reprodução / Diagnóstico de prenhez / Confirmação',
      url: '/farm/{farm}/modules/reproduction/pregnancy',
    },
    'farm-reproduction-pregnancy-rate': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'reproduction' }),
        Translate({ messageKey: 'pregnancy_diagnosis' }),
      ],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.view_conception_analytics && permissions.farm.view_service_analytics,
      title: Translate({ messageKey: 'pregnancy_rate' }),
      track: 'Reprodução / Diagnóstico de prenhez / Taxa de prenhez',
      url: '/farm/{farm}/modules/reproduction/pregnancy/rate',
    },
    'farm-reproduction-pregnancy-reproductive-evaluation': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'reproduction' })],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.list_animal && permissions.farm.list_reproduction_event,
      title: Translate({ messageKey: 'reproductive_evaluation' }),
      track: 'Reprodução / Diagnóstico de prenhez / Avaliação reprodutiva',
      url: '/farm/{farm}/modules/reproduction/reproductive-evaluation',
    },
    'farm-reproduction-pregnancy-suspected-losses': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'reproduction' }),
        Translate({ messageKey: 'pregnancy_diagnosis' }),
      ],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.list_animal && permissions.farm.list_reproduction_event,
      title: Translate({ messageKey: 'suspected_loss' }),
      track: 'Reprodução / Diagnóstico de prenhez / Suspeita de perdas',
      url: '/farm/{farm}/modules/reproduction/pregnancy/suspected',
    },
    'farm-reproduction-protocol-detail-animals': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'reproduction' }),
        'farm-reproduction-protocols',
        '{reproduction_protocol}',
      ],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.list_protocol_schedule && permissions.farm.list_animal,
      title: Translate({ messageKey: 'protocoled_animals' }),
      track: 'Reprodução / Protocolos / Protocolo / Animais protocolados',
      url: '/farm/{farm}/modules/reproduction/protocols/{reproduction_protocol}/detail',
    },
    'farm-reproduction-protocol-detail-results': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'reproduction' }),
        'farm-reproduction-protocols',
        '{reproduction_protocol}',
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_protocol_schedule,
      title: Translate({ messageKey: 'results_analysis' }),
      track: 'Reprodução / Protocolos / Protocolo / Análise de resultados',
      url: '/farm/{farm}/modules/reproduction/protocols/{reproduction_protocol}/detail/results',
    },
    'farm-reproduction-protocol-detail-schedule': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'reproduction' }),
        'farm-reproduction-protocols',
        '{reproduction_protocol}',
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_protocol_schedule,
      title: Translate({ messageKey: 'schedule_protocol' }),
      track: 'Reprodução / Protocolos / Protocolo / Programação do protocolo',
      url: '/farm/{farm}/modules/reproduction/protocols/{reproduction_protocol}/detail/schedule',
    },
    'farm-reproduction-protocol-marked': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'reproduction' }),
        Translate({ messageKey: 'protocol_plural' }),
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_service_analytics,
      title: Translate({ messageKey: 'marked_for_protocol_plural' }),
      track: 'Reprodução / Protocolos / Marcadas para protocolo',
      url: '/farm/{farm}/modules/reproduction/protocols/marked',
    },
    'farm-reproduction-protocol-model': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'reproduction' }),
        Translate({ messageKey: 'protocol_plural' }),
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_protocol_model,
      title: Translate({ messageKey: 'model_protocol' }),
      track: 'Reprodução / Protocolos / Modelo de protocolo',
      url: '/farm/{farm}/modules/reproduction/protocols/model',
    },
    'farm-reproduction-protocol-removed-animals': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'reproduction' }),
        'farm-reproduction-protocols',
        '{reproduction_protocol}',
      ],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.list_protocol_schedule && permissions.farm.list_animal,
      title: Translate({ messageKey: 'removed_animals' }),
      track: ' Reprodução / Protocolos / Animais removidos',
      url: '/farm/{farm}/modules/reproduction/protocols/{reproduction_protocol}/detail/removed',
    },
    'farm-reproduction-protocols': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'reproduction' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_protocol_schedule,
      title: Translate({ messageKey: 'my_protocols' }),
      track: 'Reprodução / Protocolos / Meus protocolos',
      url: '/farm/{farm}/modules/reproduction/protocols',
    },
    'farm-settings-handling': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'farm_settings' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_config_farm,
      title: Translate({ messageKey: 'farm_management' }),
      track: 'Configurações da fazenda / Manejo da fazenda',
      url: '/farm/{farm}/settings/handling',
    },
    'farm-settings-imports': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'farm_settings' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_config_farm_import,
      title: Translate({ messageKey: 'imports' }),
      track: 'Configurações da fazenda / Importações',
      url: '/farm/{farm}/settings/imports',
    },
    'farm-settings-monitoring': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'farm_settings' })],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.manage_config_farm_monitoring ||
        permissions.farm.list_config_farm_monitoring,
      title: Translate({ messageKey: 'monitoring' }),
      track: 'Configurações da fazenda / Monitoramento',
      url: '/farm/{farm}/settings/monitoring',
    },
    'farm-settings-network': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'farm_settings' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_networking_farm,
      title: Translate({ messageKey: 'network' }),
      track: 'Configurações da fazenda / Rede',
      url: '/farm/{farm}/settings/network',
    },
    'farm-settings-okr': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'farm_settings' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_config_farm,
      title: Translate({ messageKey: 'farm_OKR' }),
      track: 'Configurações da fazenda / OKR',
      url: '/farm/{farm}/settings/okr',
    },
    'farm-settings-overview': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'farm_settings' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_config_farm,
      title: Translate({ messageKey: 'overview' }),
      track: 'Configurações da fazenda / Visão geral',
      url: '/farm/{farm}/settings',
    },
    'farm-settings-roles': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'farm_settings' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_config_farm_roles,
      title: Translate({ messageKey: 'role_plural' }),
      track: 'Configurações da fazenda / Papéis',
      url: '/farm/{farm}/settings/roles',
    },
    'farm-settings-units': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'farm_settings' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_config_farm_unit,
      title: Translate({ messageKey: 'unit_plural' }),
      track: 'Configurações da fazenda / Unidades',
      url: '/farm/{farm}/settings/units',
    },
    'farm-settings-users': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'farm_settings' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_config_farm,
      title: Translate({ messageKey: 'user_plural' }),
      track: 'Configurações da fazenda / Usuários',
      url: '/farm/{farm}/settings/users',
    },
    'farm-settings-vic': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'farm_settings' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_config_farm,
      title: Translate({ messageKey: 'VIC' }),
      track: 'Configurações da fazenda / VIC',
      url: '/farm/{farm}/settings/vic',
    },
    'farm-thermal-stress-hourly': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', 'farm-thermal-stress-overview'],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_temperature_history,
      title: Translate({ messageKey: 'hourly' }),
      track: 'Estresse térmico / Hora/hora',
      url: '/farm/{farm}/modules/thermal-stress/{point}/hourly',
    },
    'farm-thermal-stress-overview': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', Translate({ messageKey: 'thermal_stress' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.view_temperature_history,
      title: Translate({ messageKey: 'overview' }),
      track: 'Estresse térmico / Visão geral',
      url: '/farm/{farm}/modules/thermal-stress',
    },
    'farm-thermal-stress-real-time': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}', 'farm-thermal-stress-overview'],
      description: '',
      enabled: ({ permissions, point }) =>
        point !== 'regional-station' &&
        permissions.farm.list_device_point &&
        permissions.farm.view_real_time_thi, // TODO
      title: Translate({ messageKey: 'real_time' }),
      track: 'Estresse térmico / Tempo real',
      url: '/farm/{farm}/modules/thermal-stress/{point}/real-time',
    },
    'farm-user-info': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'user' }),
        Translate({ messageKey: 'user_environment' }),
      ],
      description: '',
      enabled: () => true, // Revisado
      title: Translate({ messageKey: 'user_informations' }),
      track: 'Usuário / Informações do usuário',
      url: '/farm/{farm}/user/settings',
    },
    'farm-user-notifications': {
      category: 'resources',
      context: 'farm',
      crumbs: [
        '{farm}',
        Translate({ messageKey: 'user' }),
        Translate({ messageKey: 'user_environment' }),
      ],
      description: '',
      enabled: () => true, // Revisado
      title: Translate({ messageKey: 'notification_plural' }),
      track: 'Usuário / Notificações',
      url: '/farm/{farm}/user/notifications',
    },
    'farm-vic': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}'],
      description: '',
      enabled: ({ permissions }) =>
        permissions.farm.use_vic_gpt_cowmed || permissions.farm.use_vic_gpt,
      title: Translate({ messageKey: 'vic_gpt_title' }),
      track: 'Vic GPT',
      url: '/farm/{farm}/vic',
    },
    'farm-vic-routines': {
      category: 'resources',
      context: 'farm',
      crumbs: ['{farm}'],
      description: '',
      enabled: () => true, // Revisado
      title: Translate({ messageKey: 'vic_ai_title' }),
      track: 'Rotinas',
      url: '/farm/{farm}/modules/routine',
    },
    'guest-bem-estar-frankanna': {
      category: 'resources',
      context: 'guest',
      crumbs: [],
      description: '',
      enabled: () => true,
      // Landing page fixa em pt-BR, sem i18n. TODO REMOVER TUDO DESSA ROTA EM SETEMBRO
      title: 'Bem-estar',
      track: 'Bem-estar',
      url: '/bem-estar/frankanna',
    },
    'guest-logout': {
      category: 'resources',
      context: 'guest',
      crumbs: [],
      description: '',
      enabled: () => true,
      title: Translate({ messageKey: 'logout' }),
      track: 'Sair',
      url: '/logout',
    },
    'guest-maintenance': {
      category: 'resources',
      context: 'guest',
      crumbs: [],
      description: '',
      enabled: () => true,
      title: Translate({ messageKey: 'site_in_maintenance' }),
      track: 'Manutenção',
      url: '/maintenance',
    },
    'guest-not-found': {
      category: 'resources',
      context: 'guest',
      crumbs: [],
      description: '',
      enabled: () => true,
      title: Translate({ messageKey: '404_fallback_title' }),
      track: 'Página não encontrada',
      url: '/404',
    },
    'guest-suspended-account': {
      category: 'resources',
      context: 'guest',
      crumbs: [],
      description: '',
      enabled: () => true,
      title: Translate({ messageKey: 'suspended_account_title' }),
      track: 'Conta suspensa',
      url: '/suspended-account',
    },
    'system-access-management-modules': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'access_management' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_module,
      title: Translate({ messageKey: 'module_plural' }),
      track: 'Gestão de acessos / Módulos',
      url: '/system/access-management/modules',
    },
    'system-access-management-permissions': {
      category: 'resources',
      context: 'system',
      crumbs: [
        Translate({ messageKey: 'access_management' }),
        Translate({ messageKey: 'permission_plural' }),
      ],
      description: '',
      enabled: ({ permissions }) => permissions.system.update_permission,
      title: Translate({ messageKey: 'permissions_list' }),
      track: 'Gestão de acessos / Permissões / Lista de permissões',
      url: '/system/access-management/permissions',
    },
    'system-access-management-permissions-b2c': {
      category: 'resources',
      context: 'system',
      crumbs: [
        Translate({ messageKey: 'access_management' }),
        Translate({ messageKey: 'permission_plural' }),
      ],
      description: '',
      enabled: ({ permissions }) => permissions.system.update_route_permission,
      title: Translate({ messageKey: 'b2c_screens' }),
      track: 'Gestão de acessos / Permissões / Telas B2C',
      url: '/system/access-management/permissions/b2c',
    },
    'system-access-management-roles': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'access_management' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_role,
      title: Translate({ messageKey: 'role_plural' }),
      track: 'Gestão de acessos / Papéis',
      url: '/system/access-management/roles',
    },
    'system-access-management-system-roles': {
      category: 'resources',
      context: 'system',
      crumbs: [
        Translate({ messageKey: 'access_management' }),
        Translate({ messageKey: 'system_users' }),
      ],
      description: '',
      enabled: ({ permissions }) => permissions.system.manage_system_user,
      title: Translate({ messageKey: 'role_plural' }),
      track: 'Gestão de acessos / Usuários do sistema / Papéis',
      url: '/system/access-management/system-users/roles',
    },
    'system-access-management-system-users': {
      category: 'resources',
      context: 'system',
      crumbs: [
        Translate({ messageKey: 'access_management' }),
        Translate({ messageKey: 'system_users' }),
      ],
      description: '',
      enabled: ({ permissions }) => permissions.system.manage_system_user,
      title: Translate({ messageKey: 'user_plural' }),
      track: 'Gestão de acessos / Usuários do sistema / Usuários',
      url: '/system/access-management/system-users/users',
    },
    'system-accesses': {
      category: 'resources',
      context: 'system',
      crumbs: [],
      description: '',
      enabled: () => true, // Revisado
      title: Translate({ messageKey: 'accesses' }),
      track: 'Acessos',
      url: '/',
    },
    'system-accounts-companies': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'account_plural' })],
      description: '',
      enabled: ({ has_company, permissions }) => has_company && permissions.system.list_company,
      title: Translate({ messageKey: 'company_plural' }),
      track: 'Contas / Empresas',
      url: '/accounts/company',
    },
    'system-accounts-company-customization': {
      category: 'resources',
      context: 'company',
      crumbs: [
        Translate({ messageKey: 'account_plural' }),
        'system-accounts-companies',
        '{company}',
      ],
      description: '',
      enabled: ({ permissions }) => permissions.company.update_business_configuration,
      title: Translate({ messageKey: 'customization' }),
      track: 'Contas / Empresas / Empresa / Personalização',
      url: '/accounts/company/{company}/customization',
    },
    'system-accounts-company-extra-modules': {
      category: 'resources',
      context: 'system',
      crumbs: [
        Translate({ messageKey: 'account_plural' }),
        'system-accounts-companies',
        '{company}',
      ],
      description: '',
      enabled: ({ permissions }) =>
        permissions.company.list_company_module &&
        permissions.company.update_company &&
        permissions.company.relate_company_module &&
        permissions.company.unrelate_company_module,
      title: Translate({ messageKey: 'extra_modules' }),
      track: 'Contas / Empresas / Módulos extras',
      url: '/accounts/company/{company}/extra-modules',
    },
    'system-accounts-company-okr': {
      category: 'resources',
      context: 'company',
      crumbs: [
        Translate({ messageKey: 'account_plural' }),
        'system-accounts-companies',
        '{company}',
      ],
      description: '',
      enabled: ({ permissions }) => permissions.company.list_company_goal,
      title: Translate({ messageKey: 'farm_OKR' }),
      track: 'Contas / Empresas /  Okr',
      url: '/accounts/company/{company}/okr',
    },
    'system-accounts-company-overview': {
      category: 'resources',
      context: 'company',
      crumbs: [
        Translate({ messageKey: 'account_plural' }),
        'system-accounts-companies',
        '{company}',
      ],
      description: '',
      enabled: ({ permissions }) =>
        permissions.system.list_company_user || permissions.company.list_company_user,
      title: Translate({ messageKey: 'registration_data' }),
      track: 'Contas / Empresas / Empresa / Dados cadastrais',
      url: '/accounts/company/{company}',
    },
    'system-accounts-company-portfolio': {
      category: 'resources',
      context: 'company',
      crumbs: [
        Translate({ messageKey: 'account_plural' }),
        'system-accounts-companies',
        '{company}',
      ],
      description: '',
      enabled: ({ permissions }) => permissions.company.list_company_portfolio,
      title: Translate({ messageKey: 'portfolio' }),
      track: 'Contas / Empresas / Empresa / Carteira',
      url: '/accounts/company/{company}/portfolio',
    },

    'system-accounts-company-project-details-contracts': {
      category: 'resources',
      context: 'company',
      crumbs: [
        Translate({ messageKey: 'account_plural' }),
        'system-accounts-companies',
        '{company}',
        'system-accounts-company-projects',
        '{company_project}',
      ],
      description: '',
      enabled: ({ permissions }) => permissions.company.list_company_project,
      title: Translate({ messageKey: 'contract_plural' }),
      track: 'Contas / Empresas / Empresa / Projetos / Detalhes / Contratos',
      url: '/accounts/company/{company}/projects/{company_project}/contracts',
    },
    'system-accounts-company-project-details-informations': {
      category: 'resources',
      context: 'company',
      crumbs: [
        Translate({ messageKey: 'account_plural' }),
        'system-accounts-companies',
        '{company}',
        'system-accounts-company-projects',
        '{company_project}',
      ],
      description: '',
      enabled: ({ permissions }) => permissions.company.list_company_project,
      title: Translate({ messageKey: 'project_informations' }),
      track: 'Contas / Empresas / Empresa / Projetos / Detalhes / Informações',
      url: '/accounts/company/{company}/projects/{company_project}/informations',
    },
    'system-accounts-company-projects': {
      category: 'resources',
      context: 'company',
      crumbs: [
        Translate({ messageKey: 'account_plural' }),
        'system-accounts-companies',
        '{company}',
      ],
      description: '',
      enabled: ({ permissions }) => permissions.company.list_company_project,
      title: Translate({ messageKey: 'projects' }),
      track: 'Contas / Empresas / Empresa / Projetos',
      url: '/accounts/company/{company}/projects',
    },
    'system-accounts-company-roles': {
      category: 'resources',
      context: 'company',
      crumbs: [
        Translate({ messageKey: 'account_plural' }),
        'system-accounts-companies',
        '{company}',
      ],
      description: '',
      enabled: ({ permissions }) => permissions.company.list_company_role,
      title: Translate({ messageKey: 'role_plural' }),
      track: 'Contas / Empresas / Empresa / Papéis',
      url: '/accounts/company/{company}/roles',
    },
    'system-accounts-company-users': {
      category: 'resources',
      context: 'company',
      crumbs: [
        Translate({ messageKey: 'account_plural' }),
        'system-accounts-companies',
        '{company}',
      ],
      description: '',
      enabled: ({ permissions }) => permissions.company.list_company_user,
      title: Translate({ messageKey: 'user_plural' }),
      track: 'Contas / Empresas / Empresa / Usuários',
      url: '/accounts/company/{company}/users',
    },
    'system-accounts-farm-contract': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'account_plural' }), 'system-accounts-farms', '{farm}'],
      description: '',
      enabled: ({ context, permissions }) =>
        context === 'system'
          ? permissions.system.list_account_farm
          : permissions.farm.list_account_farm,
      title: Translate({ messageKey: 'contract' }),
      track: 'Contas / Fazendas / Fazenda / Contrato',
      url: '/accounts/farm/{farm}/contract',
    },
    'system-accounts-farm-details': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'account_plural' }), 'system-accounts-farms', '{farm}'],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_account_farm,
      title: Translate({ messageKey: 'registration_data' }),
      track: 'Contas / Fazendas / Fazenda / Dados cadastrais',
      url: '/accounts/farm/{farm}',
    },
    'system-accounts-farm-extra-modules': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'account_plural' }), 'system-accounts-farms', '{farm}'],
      description: '',
      enabled: ({ permissions }) =>
        permissions.system.list_extramodules_account_farm ||
        permissions.farm.list_extramodules_account_farm,
      title: Translate({ messageKey: 'extra_modules' }),
      track: 'Contas / Fazendas / Fazenda / Módulos extras',
      url: '/accounts/farm/{farm}/extra-modules',
    },
    'system-accounts-farm-network': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'account_plural' }), 'system-accounts-farms', '{farm}'],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_networking_farm,
      title: Translate({ messageKey: 'network' }),
      track: 'Contas / Fazendas / Fazenda / Rede',
      url: '/accounts/farm/{farm}/network',
    },
    'system-accounts-farms': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'account_plural' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_account_farm,
      title: Translate({ messageKey: 'farm_plural' }),
      track: 'Contas / Fazendas',
      url: '/accounts/farm',
    },
    'system-accounts-renewals': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'account_plural' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_account_farm,
      title: Translate({ messageKey: 'renewals' }),
      track: 'Contas / Renovações',
      url: '/accounts/renewals',
    },
    'system-ai-rags': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'ai' }), Translate({ messageKey: 'rags' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.view_rag,
      title: Translate({ messageKey: 'resources' }),
      track: 'IA / RAGS / Recursos',
      url: '/system/ai/rags',
    },
    'system-ai-rags-fixed': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'ai' }), Translate({ messageKey: 'rags' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.view_rag,
      title: Translate({ messageKey: 'fixed' }),
      track: 'IA / RAGS / Fixas',
      url: '/system/ai/rags/fixed',
    },
    'system-ai-tokens': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'ai' }), Translate({ messageKey: 'token_consumption' })],
      description: '',
      enabled: ({ permissions }) =>
        permissions.system.view_token_consumption || permissions.system.manage_token_consumption,
      title: Translate({ messageKey: 'farm_plural' }),
      track: 'IA / Consumo de tokens / Fazendas',
      url: '/system/ai/tokens',
    },
    'system-ai-tokens-all': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'ai' }), Translate({ messageKey: 'token_consumption' })],
      description: '',
      enabled: ({ permissions }) =>
        permissions.system.view_token_consumption || permissions.system.manage_token_consumption,
      title: Translate({ messageKey: 'total_consumption' }),
      track: 'IA / Consumo de tokens / Consumo total',
      url: '/system/ai/tokens/all',
    },
    'system-ai-tokens-farm': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'ai' }), '{farm}'],
      description: '',
      enabled: ({ permissions }) =>
        permissions.system.view_token_consumption || permissions.system.manage_token_consumption,
      title: Translate({ messageKey: 'token_consumption' }),
      track: 'IA / Consumo de tokens / Fazenda',
      url: '/system/ai/tokens/farm/{farm}',
    },
    'system-bull-semen': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'farm_database' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_semen,
      title: Translate({ messageKey: 'bull_or_semen' }) + ' (Atual)',
      track: 'Touro/Sêmen (Atual)',
      url: '/database/semen',
    },
    'system-bull-semen-companies': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'farm_database' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_semen_company,
      title: Translate({ messageKey: 'company_plural' }),
      track: 'Touro/Sêmen / Empresas',
      url: '/database/semen/companies',
    },
    'system-bull-semen-new': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'farm_database' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_new_semen,
      title: Translate({ messageKey: 'bull_or_semen' }) + ' (Futura)',
      track: 'Touro/Sêmen (FUTURA)',
      url: '/database/semen-new',
    },
    'system-c-com-device-list': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'c_com' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_ccom,
      title: Translate({ messageKey: 'device_list' }),
      track: 'C-Com / Lista de dispositivos',
      url: '/system/inventory/c-com',
    },
    'system-c-com-farm-alerts': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'c_com' }), 'system-c-com-status', '{farm}'],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_ccom,
      title: Translate({ messageKey: 'alert_plural' }),
      track: 'C-Com / Status operacional / Fazenda /Alertas',
      url: '/system/inventory/c-com/farm/{farm}/alerts',
    },
    'system-c-com-farm-connectivity': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'c_com' }), 'system-c-com-status', '{farm}'],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_ccom,
      title: Translate({ messageKey: 'connectivity' }),
      track: 'C-Com / Status operacional / Fazenda /Conectividade',
      url: '/system/inventory/c-com/farm/{farm}/connectivity',
    },
    'system-c-com-farm-data-interval': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'c_com' }), 'system-c-com-status', '{farm}'],
      description: '',
      enabled: ({ permissions }) =>
        permissions.system.view_batch_animal_collar_delay ||
        permissions.farm.view_batch_animal_collar_delay,
      title: Translate({ messageKey: 'data_interval_per_batch' }),
      track: 'C-Com / Status operacional / Fazenda /Intervalo de dados por lote',
      url: '/system/inventory/c-com/farm/{farm}/data-inverval',
    },
    'system-c-com-farm-history': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'c_com' }), 'system-c-com-status', '{farm}'],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_ccom,
      title: Translate({
        messageKey: 'item_history',
        params: { item: Translate({ lower: true, messageKey: 'storage' }) },
      }),
      track: 'C-Com / Status operacional / Fazenda /Histórico de estoque',
      url: '/system/inventory/c-com/farm/{farm}/history',
    },
    'system-c-com-farm-list': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'c_com' }), 'system-c-com-status', '{farm}'],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_ccom,
      title: Translate({ messageKey: 'c_com_plural' }),
      track: 'C-Com / Status operacional / Fazenda /C-Coms',
      url: '/system/inventory/c-com/farm/{farm}/list',
    },
    'system-c-com-farm-notes': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'c_com' }), 'system-c-com-status', '{farm}'],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_tech_support_note,
      title: Translate({ messageKey: 'support_notes' }),
      track: 'C-Com / Status operacional / Fazenda /Notas de suporte',
      url: '/system/inventory/c-com/farm/{farm}/notes',
    },
    'system-c-com-farm-overview': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'c_com' }), 'system-c-com-status', '{farm}'],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_ccom || permissions.farm.list_ccom,
      title: Translate({ messageKey: 'overview' }),
      track: 'C-Com / Status operacional / Fazenda /Visão geral',
      url: '/system/inventory/c-com/farm/{farm}',
    },
    'system-c-com-status': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'c_com' }), Translate({ messageKey: 'operational_status' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_ccom,
      title: Translate({ messageKey: 'general_status' }),
      track: 'C-Com / Status operacional',
      url: '/system/inventory/c-com/status',
    },
    'system-c-com-status-disconnected': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'c_com' }), Translate({ messageKey: 'operational_status' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_ccom,
      title: Translate({ messageKey: 'disconnected' }),
      track: 'C-Com / Disconnected',
      url: '/system/inventory/c-com/status/disconnected',
    },
    'system-c-com-status-reconnected': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'c_com' }), Translate({ messageKey: 'operational_status' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_ccom,
      title: Translate({ messageKey: ['reconnected', '24h'], translate: [true, false] }),
      track: 'C-Com / Reconnected',
      url: '/system/inventory/c-com/status/reconnected',
    },
    'system-c-tech-break': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'c_tech' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_collar,
      title: Translate({ messageKey: 'break' }),
      track: 'C-Tech / Quebra',
      url: '/system/inventory/c-tech/break',
    },
    'system-c-tech-break-farm-distribution': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'c_tech' }), 'system-c-tech-break'],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_collar,
      title: Translate({ messageKey: 'farm_distribution' }),
      track: 'C-Tech / Quebra / Distribuição em fazenda',
      url: '/system/inventory/c-tech/break/{ctech_version}/{ctech_manufacture}/farm-distribution',
    },
    'system-c-tech-collar-status': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'c_tech' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_collar,
      title: Translate({ messageKey: 'device_status' }),
      track: 'C-Tech / Status dos dispositivos',
      url: '/system/inventory/c-tech/list/status',
    },
    'system-c-tech-device-list': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'c_tech' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_collar,
      title: Translate({ messageKey: 'device_list' }),
      track: 'C-Tech / Lista de dispositivos',
      url: '/system/inventory/c-tech/list',
    },
    'system-c-tech-status': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'c_tech' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_collar_operational_status,
      title: Translate({ messageKey: 'operational_status' }),
      track: 'C-Tech / Status operacional',
      url: '/system/inventory/c-tech/status',
    },
    'system-cs-bull-migration': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'cs' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_semen,
      title: Translate({ messageKey: 'bull_migration' }),
      track: 'CS / Migração de touros',
      url: '/cs/bull-migration',
    },
    'system-cs-customer-center': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'cs' }), Translate({ messageKey: 'customer_center' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_account_farm_cs,
      title: Translate({ messageKey: 'farm_account_plural' }),
      track: 'CS / Central do cliente / Contas fazenda',
      url: '/cs/customer-center',
    },
    'system-cs-customer-center-data-update': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'cs' }), Translate({ messageKey: 'customer_center' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_account_farm_cs,
      title: Translate({ messageKey: 'data_update' }),
      track: 'CS / Central do cliente / Atualização de dados',
      url: '/cs/customer-center/data-update',
    },
    'system-cs-customer-center-farm-engangement': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'cs' }), Translate({ messageKey: 'customer_center' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_user_activity,
      title: Translate({ messageKey: 'engagement' }),
      track: 'CS / Central do cliente / Engajamento',
      url: '/cs/customer-center/relationship/{farm}/engangement',
    },
    'system-cs-customer-center-farm-profile': {
      category: 'resources',
      context: 'system',
      crumbs: [
        Translate({ messageKey: 'cs' }),
        Translate({ messageKey: 'customer_center' }),
        '{farm}',
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_account_farm_relationship,
      title: Translate({ messageKey: 'farm_profile' }),
      track: 'CS / Central do cliente / Fazenda / Perfil da fazenda',
      url: '/cs/customer-center/relationship/{farm}/farm-profile',
    },
    'system-cs-customer-center-farm-relationship': {
      category: 'resources',
      context: 'system',
      crumbs: [
        Translate({ messageKey: 'cs' }),
        Translate({ messageKey: 'customer_center' }),
        '{farm}',
      ],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_account_farm_cs,
      title: Translate({ messageKey: 'relationship' }),
      track: 'CS / Central do cliente / Fazenda / Relacionamento',
      url: '/cs/customer-center/relationship/{farm}/relationship',
    },
    'system-cs-customer-center-relationship-accesses': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'cs' }), Translate({ messageKey: 'customer_center' })],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_user_activity,
      title: Translate({ messageKey: 'accesses' }),
      track: 'CS / Central do cliente / Acessos',
      url: '/cs/customer-center/relationship/{farm}/accesses',
    },
    'system-cs-integrations-api': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'cs' }), Translate({ messageKey: 'integrations' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_account_farm_integrations,
      title: Translate({ messageKey: 'api' }),
      track: 'CS / Integrações / Api',
      url: '/cs/integrations/api',
    },
    'system-cs-integrations-cowsync': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'cs' }), Translate({ messageKey: 'integrations' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_account_farm_integrations,
      title: Translate({ messageKey: 'cowsync' }),
      track: 'CS / Integrações / Cowsync',
      url: '/cs/integrations/cowsync',
    },
    'system-cs-nps-campaign-answers': {
      category: 'resources',
      context: 'system',
      crumbs: [
        Translate({ messageKey: 'cs' }),
        Translate({ messageKey: 'nps', upper: true }),
        'system-cs-nps-campaigns',
        '{nps}',
      ],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_account_farm_nps,
      title: Translate({ messageKey: 'farm_answers' }),
      track: 'CS / NPS / Campanhas / Respostas fazendas',
      url: '/cs/nps/campaigns/{nps}/answers',
    },
    'system-cs-nps-campaign-companies-answered': {
      category: 'resources',
      context: 'system',
      crumbs: [
        Translate({ messageKey: 'cs' }),
        Translate({ messageKey: 'nps', upper: true }),
        'system-cs-nps-campaigns',
        '{nps}',
      ],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_account_farm_nps,
      title: Translate({ messageKey: 'company_answers' }),
      track: 'CS / NPS / Campanhas / Respostas empresas',
      url: '/cs/nps/campaigns/{nps}/companies-answered',
    },
    'system-cs-nps-campaign-companies-unanswered': {
      category: 'resources',
      context: 'system',
      crumbs: [
        Translate({ messageKey: 'cs' }),
        Translate({ messageKey: 'nps', upper: true }),
        'system-cs-nps-campaigns',
        '{nps}',
      ],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_account_farm_nps,
      title: Translate({ messageKey: 'unanswered_companies' }),
      track: 'CS / NPS / Campanhas / Empresas sem resposta',
      url: '/cs/nps/campaigns/{nps}/companies-unanswered',
    },
    'system-cs-nps-campaign-unanswered': {
      category: 'resources',
      context: 'system',
      crumbs: [
        Translate({ messageKey: 'cs' }),
        Translate({ messageKey: 'nps', upper: true }),
        'system-cs-nps-campaigns',
        '{nps}',
      ],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_account_farm_nps,
      title: Translate({ messageKey: 'unanswered_farms' }),
      track: 'CS / NPS / Campanhas / Fazendas sem resposta',
      url: '/cs/nps/campaigns/{nps}/unanswered',
    },
    'system-cs-nps-campaigns': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'cs' }), Translate({ messageKey: 'nps', upper: true })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_account_farm_nps,
      title: Translate({ messageKey: 'campaigns' }),
      track: 'CS / NPS / Campanhas',
      url: '/cs/nps/campaigns',
    },
    'system-data-interval-farm-overview': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'c_com' }), Translate({ messageKey: 'data_interval' })],
      description: '',
      enabled: ({ permissions }) =>
        permissions.system.view_farm_data_delays || permissions.system.view_batch_data_delays,
      title: Translate({ messageKey: 'general_farm' }),
      track: 'Intervalo de dados / Geral da fazenda',
      url: '/system/inventory/c-com/interval',
    },
    'system-data-interval-monitored-batches': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'c_com' }), Translate({ messageKey: 'data_interval' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.view_batch_data_delays,
      title: Translate({
        messageKey: 'item_monitored_plural',
        params: { item: Translate({ lower: true, messageKey: 'batch_plural' }) },
      }),
      track: 'Intervalo de dados / Lotes monitorados',
      url: '/system/inventory/c-com/interval/batch',
    },
    'system-data-view': {
      category: 'resources',
      context: 'system',
      crumbs: [''],
      description: '',
      enabled: ({ permissions }) => permissions.system.view_data_view,
      title: Translate({ messageKey: 'data_view' }),
      track: 'Data view',
      url: '/data-view',
    },
    'system-data-view-benchmarking-average-dim': {
      category: 'resources',
      context: 'system',
      crumbs: ['system-data-view', 'Benchmarking'],
      description: '',
      enabled: () => false,
      title: Translate({ messageKey: 'average_DEL' }),
      track: 'Data view / Benchmarking / DEL médio',
      url: '/data-view/benchmarking/average-del',
    },
    'system-data-view-benchmarking-conception-rate': {
      category: 'resources',
      context: 'system',
      crumbs: ['system-data-view', 'Benchmarking'],
      description: '',
      enabled: () => false,
      title: Translate({ messageKey: 'conception_rate' }),
      track: 'Data view / Benchmarking / Taxa de concepção',
      url: '/data-view/benchmarking/conception-rate',
    },
    'system-data-view-benchmarking-discard-rate': {
      category: 'resources',
      context: 'system',
      crumbs: ['system-data-view', 'Benchmarking'],
      description: '',
      enabled: () => false,
      title: Translate({ messageKey: 'discard_rate' }),
      track: 'Data view / Benchmarking / Taxa de descarte',
      url: '/data-view/benchmarking/discard-rate',
    },
    'system-data-view-benchmarking-iep': {
      category: 'resources',
      context: 'system',
      crumbs: ['system-data-view', 'Benchmarking'],
      description: '',
      enabled: () => false,
      title: Translate({ messageKey: 'calving_interval' }),
      track: 'Data view / Benchmarking / IEP',
      url: '/data-view/benchmarking/calving-interval',
    },
    'system-data-view-benchmarking-pregnancy-rate': {
      category: 'resources',
      context: 'system',
      crumbs: ['system-data-view', 'Benchmarking'],
      description: '',
      enabled: () => false,
      title: Translate({ messageKey: 'pregnancy_rate' }),
      track: 'Data view / Benchmarking / Taxa de prenhez',
      url: '/data-view/benchmarking/pregnancy-rate',
    },
    'system-data-view-benchmarking-service-rate': {
      category: 'resources',
      context: 'system',
      crumbs: ['system-data-view', 'Benchmarking'],
      description: '',
      enabled: () => false,
      title: Translate({ messageKey: 'service_rate' }),
      track: 'Data view / Benchmarking / Taxa de serviço',
      url: '/data-view/benchmarking',
    },
    'system-data-view-contracts-active': {
      category: 'resources',
      context: 'system',
      crumbs: ['system-data-view', Translate({ messageKey: 'data_view_contracts_title' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_dataview_accounts_contracts,
      title: Translate({ messageKey: 'active_contracts' }),
      track: 'Data view / Contas e contratos / Contratos ativos',
      url: '/data-view/contracts/active',
    },
    'system-data-view-contracts-lead-time-cm': {
      category: 'resources',
      context: 'system',
      crumbs: ['system-data-view', Translate({ messageKey: 'data_view_contracts_title' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.view_lead_time,
      title: Translate({ messageKey: 'lead_time_cm' }),
      track: 'Data view / Contas e contratos / Lead time CM',
      url: '/data-view/contracts/lead_time_cm',
    },
    'system-data-view-contracts-opportunities': {
      category: 'resources',
      context: 'system',
      crumbs: ['system-data-view', Translate({ messageKey: 'data_view_contracts_title' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_account_farm,
      title: Translate({ messageKey: 'opportunities' }),
      track: 'Data view / Contas e contratos / Oportunidades',
      url: '/data-view/contracts/opportunities',
    },
    'system-data-view-contracts-signed': {
      category: 'resources',
      context: 'system',
      crumbs: ['system-data-view', Translate({ messageKey: 'data_view_contracts_title' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_dataview_accounts_contracts,
      title: Translate({ messageKey: 'signed_contracts' }),
      track: 'Data view / Contas e contratos / Contratos assinados',
      url: '/data-view/contracts/signed',
    },
    'system-data-view-contracts-upsell-churn': {
      category: 'resources',
      context: 'system',
      crumbs: ['system-data-view', Translate({ messageKey: 'data_view_contracts_title' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_dataview_accounts_contracts,
      title: Translate({ messageKey: 'upsell-churn' }),
      track: 'Data view / Contas e contratos / Upsell / Churn',
      url: '/data-view/contracts/upsell-churn',
    },
    'system-import-data': {
      category: 'resources',
      context: 'system',
      crumbs: [],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_system_job,
      title: Translate({ messageKey: 'import_data' }),
      track: 'Importar dados',
      url: '/system/import',
    },
    'system-import-detail-results': {
      category: 'resources',
      context: 'system',
      crumbs: ['system-import-data', Translate({ messageKey: 'import_details' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_system_job,
      title: Translate({ messageKey: 'results' }),
      track: 'Importar dados / Detalhes da importação / Resultados',
      url: '/system/import/{import}/detail',
    },
    'system-import-detail-unrecognized-data': {
      category: 'resources',
      context: 'system',
      crumbs: ['system-import-data', Translate({ messageKey: 'import_details' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_system_job,
      title: Translate({ messageKey: 'data_not_recognized' }),
      track: 'Importar dados / Detalhes da importação / Dados não reconhecidos',
      url: '/system/import/{import}/detail/unrecognized-data',
    },
    'system-inventory-c-com-config-history': {
      category: 'resources',
      context: 'system',
      crumbs: ['system-c-com-device-list', Translate({ messageKey: 'c_com' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_ccom_configuration_history,
      title: Translate({
        messageKey: 'item_history',
        params: {
          item: Translate({ lower: true, messageKey: 'settings' }),
        },
      }),
      track: 'Inventário / C-Com / Histórico de configurações',
      url: '/system/inventory/c-com/{ccom}/config-history',
    },
    'system-inventory-c-com-farm-history': {
      category: 'resources',
      context: 'system',
      crumbs: ['system-c-com-device-list', Translate({ messageKey: 'c_com' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_ccom_farm,
      title: Translate({
        messageKey: 'item_history',
        params: {
          item: Translate({ lower: true, messageKey: 'farm_plural' }),
        },
      }),
      track: 'Inventário / C-Com / Histórico de fazendas',
      url: '/system/inventory/c-com/{ccom}/farm-history',
    },
    'system-inventory-c-com-maintenance-history': {
      category: 'resources',
      context: 'system',
      crumbs: ['system-c-com-device-list', Translate({ messageKey: 'c_com' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_ccom_maintenance_history,
      title: Translate({
        messageKey: 'item_history',
        params: {
          item: Translate({ lower: true, messageKey: 'maintenance_plural' }),
        },
      }),
      track: 'Inventário / C-Com / Histórico de manutenções',
      url: '/system/inventory/c-com/{ccom}/maintenance-history',
    },
    'system-inventory-c-com-status': {
      category: 'resources',
      context: 'system',
      crumbs: ['system-c-com-device-list', Translate({ messageKey: 'c_com' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_ccom_status,
      title: Translate({ messageKey: 'c_com_status' }),
      track: 'Inventário / C-Com / Status da c-com',
      url: '/system/inventory/c-com/{ccom}/status',
    },
    'system-inventory-c-com-status-map': {
      category: 'resources',
      context: 'system',
      crumbs: ['system-c-com-device-list', Translate({ messageKey: 'c_com' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_ccom_status,
      title: Translate({ messageKey: 'c_com_map' }),
      track: 'Inventário / C-Com / Mapa de status de c-com',
      url: '/system/inventory/c-com/{ccom}/map',
    },
    'system-inventory-c-com-status-sbc': {
      category: 'resources',
      context: 'system',
      crumbs: ['system-c-com-device-list', Translate({ messageKey: 'c_com' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_ccom_status,
      title: Translate({ messageKey: 'SBC_status' }),
      track: 'Inventário / C-Com / Status SBC',
      url: '/system/inventory/c-com/{ccom}/status-sbc',
    },
    'system-inventory-c-tech-animal-history': {
      category: 'resources',
      context: 'system',
      crumbs: [
        Translate({ messageKey: 'c_tech' }),
        Translate({ messageKey: 'device_list' }),
        '{ctech}',
      ],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_animal_collar,
      title: Translate({
        messageKey: 'item_history',
        params: { item: Translate({ lower: true, messageKey: 'animal_plural' }) },
      }),
      track: 'Inventário / C-Tech / Histórico de animais',
      url: '/system/inventory/c-tech/{ctech}/history',
    },
    'system-inventory-c-tech-collar-report-map': {
      category: 'resources',
      context: 'system',
      crumbs: [
        Translate({ messageKey: 'c_tech' }),
        Translate({ messageKey: 'device_list' }),
        '{ctech}',
      ],
      description: '',
      enabled: ({ permissions }) => permissions.system.view_monitoring,
      title: Translate({ messageKey: 'collar_map' }),
      track: 'Inventário / C-Tech / Mapa de report de coleira',
      url: '/system/inventory/c-tech/{ctech}/map',
    },
    'system-inventory-c-tech-collar-status': {
      category: 'resources',
      context: 'system',
      crumbs: [
        Translate({ messageKey: 'c_tech' }),
        Translate({ messageKey: 'device_list' }),
        '{ctech}',
      ],
      description: '',
      enabled: ({ permissions }) => permissions.system.view_collar_status,
      title: Translate({ messageKey: 'collar_status' }),
      track: 'Inventário / C-Tech / Status da coleira',
      url: '/system/inventory/c-tech/{ctech}/status',
    },
    'system-inventory-c-tech-error-history': {
      category: 'resources',
      context: 'system',
      crumbs: [
        Translate({ messageKey: 'c_tech' }),
        Translate({ messageKey: 'device_list' }),
        '{ctech}',
      ],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_collar_alert,
      title: Translate({
        messageKey: 'item_history',
        params: { item: Translate({ lower: true, messageKey: 'error_plural' }) },
      }),
      track: 'Inventário / C-Tech / Histórico de erros',
      url: '/system/inventory/c-tech/{ctech}/error-history',
    },
    'system-inventory-c-tech-farm-all-status': {
      category: 'resources',
      context: 'system',
      crumbs: [
        Translate({ messageKey: 'c_tech' }),
        Translate({ messageKey: 'operational_status' }),
        '{farm}',
      ],
      description: '',
      enabled: ({ permissions }) =>
        permissions.system.list_collar_operational_status ||
        permissions.farm.list_collar_operational_status,
      title: Translate({ messageKey: 'all_female' }),
      track: 'Inventário / C-Tech / Fazenda / Todas',
      url: '/system/inventory/c-tech/farm/{farm}/status/all',
    },
    'system-inventory-c-tech-farm-history': {
      category: 'resources',
      context: 'system',
      crumbs: [
        Translate({ messageKey: 'c_tech' }),
        Translate({ messageKey: 'device_list' }),
        '{ctech}',
      ],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_collar_farm,
      title: Translate({
        messageKey: 'item_history',
        params: { item: Translate({ lower: true, messageKey: 'transaction_plural' }) },
      }),
      track: 'Inventário / C-Tech / Histórico de movimentações',
      url: '/system/inventory/c-tech/{ctech}/farm-history',
    },
    'system-inventory-c-tech-farm-on-status-farm': {
      category: 'resources',
      context: 'system',
      crumbs: [
        Translate({ messageKey: 'c_tech' }),
        Translate({ messageKey: 'operational_status' }),
        '{farm}',
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_collar_operational_status,
      title: Translate({ messageKey: 'farm_revision' }),
      track: 'Inventário / C-Tech / Fazenda / Revisão na fazenda',
      url: '/system/inventory/c-tech/farm/{farm}/status/onFarm',
    },
    'system-inventory-c-tech-farm-status-lost': {
      category: 'resources',
      context: 'system',
      crumbs: [
        Translate({ messageKey: 'c_tech' }),
        Translate({ messageKey: 'operational_status' }),
        '{farm}',
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_collar_operational_status,
      title: Translate({ messageKey: 'lost_female_plural' }),
      track: 'Inventário / C-Tech / Fazenda / Perdidas',
      url: '/system/inventory/c-tech/farm/{farm}/status/lost',
    },
    'system-inventory-c-tech-farm-status-new': {
      category: 'resources',
      context: 'system',
      crumbs: [
        Translate({ messageKey: 'c_tech' }),
        Translate({ messageKey: 'operational_status' }),
        '{farm}',
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_collar_operational_status,
      title: Translate({ messageKey: 'new_item_female_plural' }),
      track: 'Inventário / C-Tech / Fazenda / Novas',
      url: '/system/inventory/c-tech/farm/{farm}/status/new',
    },
    'system-inventory-c-tech-farm-status-possible-removal': {
      category: 'resources',
      context: 'system',
      crumbs: [
        Translate({ messageKey: 'c_tech' }),
        Translate({ messageKey: 'operational_status' }),
        '{farm}',
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_collar_review_on_farm,
      title: Translate({ messageKey: 'possible_removal' }),
      track: 'Inventário / C-Tech / Possível remoção',
      url: '/system/inventory/c-tech/farm/{farm}/status/possible-removal',
    },
    'system-inventory-c-tech-farm-status-replacement': {
      category: 'resources',
      context: 'system',
      crumbs: [
        Translate({ messageKey: 'c_tech' }),
        Translate({ messageKey: 'operational_status' }),
        '{farm}',
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_collar_operational_status,
      title: Translate({ messageKey: 'replace' }),
      track: 'Inventário / C-Tech / Fazenda / Trocas',
      url: '/system/inventory/c-tech/farm/{farm}/status/replacement',
    },
    'system-inventory-c-tech-farm-status-waiting': {
      category: 'resources',
      context: 'system',
      crumbs: [
        Translate({ messageKey: 'c_tech' }),
        Translate({ messageKey: 'operational_status' }),
        '{farm}',
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_collar_review_on_farm,
      title: Translate({
        messageKey: 'waiting',
        params: { item: Translate({ lower: true, messageKey: 'attachment' }) },
      }),
      track: 'Inventário / C-Tech / Aguardando vínculo',
      url: '/system/inventory/c-tech/farm/{farm}/status/waiting',
    },
    'system-inventory-c-tech-farm-technical-status-revision': {
      category: 'resources',
      context: 'system',
      crumbs: [
        Translate({ messageKey: 'c_tech' }),
        Translate({ messageKey: 'operational_status' }),
        '{farm}',
      ],
      description: '',
      enabled: ({ permissions }) => permissions.farm.list_collar_operational_status,
      title: Translate({ messageKey: 'tecnical_review' }),
      track: 'Inventário / C-Tech / Fazenda / Revisão técnica',
      url: '/system/inventory/c-tech/farm/{farm}/status/technical-revision',
    },
    'system-inventory-cowcomfort-device-status': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'inventory' }), Translate({ messageKey: 'cow_comfort' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.view_cenv_status,
      title: Translate({ messageKey: 'device_status' }),
      track: 'Inventário / CowComfort / Status do dispositivo',
      url: '/system/inventory/c-env/{cenv}/status',
    },
    'system-inventory-cowcomfort-history': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'inventory' }), Translate({ messageKey: 'cow_comfort' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_cenv,
      title: Translate({
        messageKey: 'item_history',
        params: { item: Translate({ lower: true, messageKey: 'transaction_plural' }) },
      }),
      track: 'Inventário / CowComfort / Histórico de movimentações',
      url: '/system/inventory/c-env/{cenv}/history',
    },
    'system-root-cowcomfort': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'root' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_cenv,
      title: Translate({ messageKey: 'cow_comfort' }),
      track: 'CowComfort / Lista de dispositivos',
      url: '/system/inventory/c-env',
    },
    'system-root-settings-help-pages': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'root' })],
      description: '',
      enabled: ({ permissions }) =>
        permissions.system.create_help || permissions.system.update_help,
      title: Translate({ messageKey: 'help_center' }),
      track: 'Root / Central de ajuda',
      url: '/system/settings/help-pages',
    },
    'system-root-settings-mails': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'root' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_mail_recipient,
      title: Translate({ messageKey: 'transactional_emails' }),
      track: 'Root / Emails transacionais',
      url: '/system/settings/mails',
    },
    'system-root-settings-release-notes': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'root' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_release_notes,
      title: Translate({ messageKey: 'release_notes' }),
      track: 'Root / Notas da versão',
      url: '/system/settings/release-notes',
    },
    'system-tests': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'root' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.access_telescope,
      title: Translate({ messageKey: 'test_plural' }),
      track: 'Root / Testes',
      url: '/system/settings/tests',
    },
    'system-treatment-commercial-products': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'treatment_plural' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_treatment,
      title: Translate({ messageKey: 'commercial_products' }),
      track: 'Tratamentos / Produtos comerciais',
      url: '/database/treatment',
    },
    'system-treatment-manufacturers': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'treatment_plural' })],
      description: '',
      enabled: ({ permissions }) => permissions.system.list_treatment_manufacturer,
      title: Translate({ messageKey: 'manufacturer_plural' }),
      track: 'Tratamentos / Fabricantes',
      url: '/database/treatment/manufacturer',
    },
    'system-user-info': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'user' }), Translate({ messageKey: 'user_environment' })],
      description: '',
      enabled: () => true, // Revisado
      title: Translate({ messageKey: 'user_informations' }),
      track: 'Usuário / Informações do usuário',
      url: '/user/settings',
    },
    'system-user-notifications': {
      category: 'resources',
      context: 'system',
      crumbs: [Translate({ messageKey: 'user' }), Translate({ messageKey: 'user_environment' })],
      description: '',
      enabled: () => true, // Revisado
      title: Translate({ messageKey: 'notification_plural' }),
      track: 'Usuário / Notificações',
      url: '/user/notifications',
    },
  }) satisfies Record<string, PageByIdEntry>

export type PageId = keyof ReturnType<typeof getMapPages>

let cachedPagesById: Record<PageId, PageByIdEntry> | null = null

export const PAGES_BY_ID = (): Record<PageId, PageByIdEntry> => {
  if (!cachedPagesById) cachedPagesById = getMapPages()
  return cachedPagesById
}
